import { CSSProperties, useState, useRef, useEffect, Fragment } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import useScreen from 'Hooks/useScreen'
import useMaestroChat, {
    MessageItem,
    QuickAction,
    AgentJob,
    ChatConversation,
    AssetJobDetails,
    CampaignExecutorDetails,
    MessagePart
} from 'Hooks/useMaestroChat'
import useChatScroll from 'Hooks/useChatScroll'
import { useDropzone } from 'react-dropzone'
import { styx, clsx, makeKey } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import UniversalUploadWidget, { UploadedFile } from 'components/shareable/UniversalUploadWidget/UniversalUploadWidget'
import UploadedFilePreview from 'components/shareable/UploadedFilePreview/UploadedFilePreview'
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget'
import UploadButtonWithDropdown from 'components/shareable/UploadButtonWithDropdown/UploadButtonWithDropdown'
import DailyTasks from './DailyTasks';
import { AgentTaskOptions as AssistantTask } from "api/maestroTasksApi"
import useUser from 'Hooks/useUser';
import AssistantWorkspaceWebsitePreview from './AssistantWorkspaceWebsitePreview'
import AssistantWorkspaceTemplateSelection from './AssistantWorkspaceTemplateSelection'
import AssistantWorkspaceFacebookConnect from './AssistantWorkspaceFacebookConnect'
import AssistantWorkspaceMarketingPlan from './AssistantWorkspaceMarketingPlan'
import AssistantWorkspaceContentAnalysis from './AssistantWorkspaceContentAnalysis'
import AssistantMarkdownParser from './AssistantMarkdownParser'
import AssistantWorkspaceWrapper from './AssistantWorkspaceWrapper'
import ButtonSymphony from 'elements/ButtonSymphony'
import ModalSymphony from 'elements/ModalSymphony'
import AssistantWorkspaceInstagramPostSelection from './AssistantWorkspaceInstagramPostSelection'
import AssistantWorkspaceCampaignApproval from './AssistantWorkspaceCampaignApproval'
import RecordSearchView from 'elements/RecordSearchView'
import { RecordDetails, UnifiedSpotifyRecord } from 'modules/Types'
import TabsSymphony, { TabItem } from 'elements/TabsSymphony'
import TabSymphony from 'elements/TabSymphony'
import TaskPackPurchaseModal from 'components/TaskPackPurchaseModal/TaskPackPurchaseModal'
import SystemMessages from './SystemMessages'
import AssetGenerationInline from 'components/AssetGenerationInline'
import AssistantSummaryCard from './AssistantSummaryCard'
import ThinkingSection from './ThinkingSection'
import QuickActions from './QuickActions'
import TagSymphony from 'elements/TagSymphony'
import ToolCallIndicator from './ToolCallIndicator'
import MessageActions from './MessageActions'
import useBrand, { getAnonymousSessionIdFromLocalStorage } from 'Hooks/useBrand'
import { toastError } from 'modules/Toast'
import UpgradeToProModal from 'components/shareable/UpgradeToProModal/UpgradeToProModal'
import useSignupFlow, { WorkspaceType } from 'Hooks/useSignupFlow'
import MaestroMessageLimitCounter from 'components/shareable/MaestroTaskCounter/MaestroMessageLimitCounter'
import MessageUploadedAssets from './MessageUploadedAssets'
import useMessageLimits from 'Hooks/useMessageLimits'
import MessageLimitEnforcementModal from 'components/shareable/MessageLimitEnforcementModal/MessageLimitEnforcementModal'
import SignupFlowModal from 'components/shareable/SignupFlowModal/SignupFlowModal'
import usePlans from 'Hooks/usePlans'


// Workspace mapping for button display
const WORKSPACE_BUTTON_MAPPING: Record<string, { title: string; icon: string }> = {
    'templateSelection': { title: 'Choose Template', icon: 'file' },
    'facebookConnect': { title: 'Connect Facebook', icon: 'facebookFlat' },
    'instagramPostSelection': { title: 'Select Instagram Posts', icon: 'instagram' },
    'campaignPreview': { title: 'Preview Campaign', icon: 'marketing' },
    'marketingPlan': { title: 'View Marketing Plan', icon: 'document' },
    'contentAnalysis': { title: 'View Content Analysis', icon: 'analytics' },
    'website': { title: 'Open Website Builder', icon: 'website' },
    'campaign': { title: 'View Analytics', icon: 'chart' },
}






const AssistantChat = ({
    title = undefined,
    customChatComponentStyling = undefined,
}: {
    title?: string
    customChatComponentStyling?: CSSProperties
}) => {
    const history = useHistory()
    const {
        mobileView,
        mStyle,
    } = useScreen()

    // Auth0 hooks for authentication
    const { loginWithRedirect, isAuthenticated } = useAuth0()
    const { isFirstSession, currentBrand, ensureBrandAvailable } = useBrand()
    const { fetchPlans } = usePlans()

    const { isSymphonyAdmin } = useUser()
    const {
        userMessageText: messageText,
        setUserMessageText: setMessageText,
        sendMessage,
        messages: storeMessages,
        isStreaming,
        currentStreamedText,
        isLoadingAIResponse,
        clearChatHistory,

        isUploading,
        isBuilding,
        buildingMessage,
        // New streaming state
        activeWorkspace,
        setActiveWorkspace,
        // Thinking state for o4-mini reasoning summaries
        thinkingState,
        // Quick Actions
        availableQuickActions,
        clearQuickActions,
        // Dev Mode
        devMode,
        setDevMode,
        addCustomMessage,
        // Demo Mode
        demoMode,
        // Tasks & Chats
        agentJobs,
        chats,
        isLoadingJobs: isLoadingTasks,
        isLoadingChats,
        selectedJobDetails,
        showJobDetailsModal,
        setShowJobDetailsModal,
        fetchJobs,
        fetchChats,
        fetchJobDetails,
        resumeChat,
        generateChatId,
        chatId,
        setChatId,
        currentToolExecution,
        // Chat state
        isChatOpen: isChat,
        setIsChatOpen: setIsChat,
        streamingState,
        handleFeedbackSubmit,
        hasSubmittedConversationFeedback,
        showFeedbackPrompt,
    } = useMaestroChat()

    // Upload widget store
    const {
        uploadingFiles,
        uploadedAssets,
        openUniversalUploadWidget,
        retryUpload,
        updateUploadingFile,
        handleUploadError,
        clearUploadedAssets
    } = useUniversalUploadWidget()

    // Message limit enforcement
    const {
        checkMessageLimitAndEnforce,
        isEnforcementModalOpen,
        closeEnforcementModal,
        fetchMessageUsage
    } = useMessageLimits()

    // Define tabs for the interface
    // TODO: Uncomment this
    const MAESTRO_TABS: TabItem[] = [
        { id: 'recommendations', title: 'Recommendations' },
        { id: 'chats', title: 'Chats' },
        { id: 'jobs', title: 'Jobs' }
    ]

    // Tab state
    const [activeTab, setActiveTab] = useState<TabItem>(MAESTRO_TABS[0])


    // Local state for input focus tracking
    const [inputFocused, setInputFocused] = useState(false)

    // State for maestro change log modal
    const [showMaestroChangeLogModal, setShowMaestroChangeLogModal] = useState(false)

    // Character limit for textarea
    const CHARACTER_LIMIT = 4000
    const [isCharacterLimitReached, setIsCharacterLimitReached] = useState(false)

    // State for "Show thinking" dropdown
    const [thinkingProcessInitiallyOpen, setThinkingProcessInitiallyOpen] = useState(true)

    // State for music selector modal
    const [showMusicSelector, setShowMusicSelector] = useState(false)

    // State for test workspace modal
    const [showTestWorkspaceModal, setShowTestWorkspaceModal] = useState(false)
    const [testCampaignId, setTestCampaignId] = useState('')

    // State for load chat modal
    const [showLoadChatModal, setShowLoadChatModal] = useState(false)
    const [testChatId, setTestChatId] = useState('')

    // State for demo workspaces modal
    const [showDemoWorkspaceModal, setShowDemoWorkspaceModal] = useState(false)

    // State for the selected question variant (set once on mount)
    const [selectedQuestion, setSelectedQuestion] = useState<string>('')

    // State for add message modal
    const [showAddMessageModal, setShowAddMessageModal] = useState(false)
    const [newMessageText, setNewMessageText] = useState('')
    const [newMessageIsUser, setNewMessageIsUser] = useState(true)

    // --- START Lightbox State ---
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [selectedAssetForLightbox, setSelectedAssetForLightbox] = useState<UploadedFile | null>(null);
    // --- END Lightbox State ---




    // Derived workspace state from hook
    const isWorkspaceVisible = !!activeWorkspace
    const workspaceContent = activeWorkspace ? {
        type: activeWorkspace.type as 'report' | 'preview' | 'component' | 'custom' | 'website' | 'templateSelection' | 'campaignPreview' | 'facebookConnect' | 'marketingPlan' | 'instagramPostSelection' | 'contentAnalysis',
        data: activeWorkspace.data,
        title: undefined,
        isLoading: false
    } : null

    // Chat scroll hook - encapsulates all scroll logic
    const {
        messagesContainerRef,
        messagesEndRef,
        showScrollToBottom,
        scrollToBottom,
        scheduleScrollToUserMessage
    } = useChatScroll({
        isStreaming,
        currentStreamedText,
        messages: storeMessages,
        inChatMode: isChat
    })

    // Ref to track previous message count for chat resumption detection
    const prevMessageCountRef = useRef(0)

    // Ref for the upload button

    // Ref to track previous isChat state for cleanup
    const prevIsChatRef = useRef(isChat)

    // State for external files to pass to UniversalUploadWidget
    const [externalFilesToUpload, setExternalFilesToUpload] = useState<File[]>([]);

    // --- START Drag and Drop Upload Setup ---
    const onDrop = async (acceptedFiles: File[]) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return;

        // Set files to be processed by UniversalUploadWidget
        setExternalFilesToUpload(acceptedFiles);

        // Switch to chat mode if not already
        // if (!isChat) {
        //     pushToChatPage();
        // }
        // setIsChat(true);

        // Clear the external files after a brief delay to reset the prop
        setTimeout(() => {
            setExternalFilesToUpload([]);
        }, 100);
    };

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
            'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
            'audio/*': ['.mp3', '.wav', '.ogg', '.aac'],
            'application/pdf': ['.pdf'],
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'text/plain': ['.txt'],
        },
        noClick: true,
        noKeyboard: true,
        multiple: true,
    });
    // --- END Drag and Drop Upload Setup ---


    // Set the question variant once on mount
    useEffect(() => {
        if (!selectedQuestion) {
            const questionVariants = [
                "What's our primary marketing goal today?",
                "What marketing campaign should we create?",
                "Let's build a marketing plan. What's our objective?",
                `How can I help with your marketing strategy?`,
                "Need a new ad campaign? Tell me about your product or service.",
                "Ready to launch a new promotion?",
                `How can we grow your audience today?`,
                "What content should we create to promote your brand?",
                "Let's define our target audience for the next campaign.",
                "What's the big marketing idea we should execute?",
                "Let's get your brand noticed. What's our first step?",
                "Describe the product or release you want to market.",
                "What marketing materials do we need to create today?",
                `What's the key message for our next campaign?`,
                `Time to run some ads. What are we advertising?`,
                "How can we boost your online presence?",
                "Let's analyze your marketing performance. Where should we start?",
                `Ready to brainstorm content ideas?`,
                "What's your marketing focus right now?",
                "Tell me what you want to market, and I'll build a plan."
            ]
            const randomQuestion = questionVariants[Math.floor(Math.random() * questionVariants.length)]
            setSelectedQuestion(randomQuestion)
        }
    }, [selectedQuestion])





    // Effect to manage the thinking process visibility
    useEffect(() => {
        if (isStreaming) {
            if (currentStreamedText) {
                // Text has started streaming, collapse the thinking process
                setThinkingProcessInitiallyOpen(false)
            } else if (thinkingState.isThinking || thinkingState.currentThinking) {
                // AI is thinking or has thinking content, keep it open
                setThinkingProcessInitiallyOpen(true)
            } else {
                // Still in pre-streaming phase, keep it open
                setThinkingProcessInitiallyOpen(true)
            }
        }
    }, [isStreaming, currentStreamedText, thinkingState.isThinking, thinkingState.currentThinking])

    // Add animation keyframes style
    useEffect(() => {
        // Create style element for animation
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
        .assistantChatMessageIcon.waiting {
            animation: pulse 1.5s ease-in-out infinite;
        }

        .assistantChatMessageIcon {
            animation: none;
        }

      @keyframes typingAnimation {
        0%, 80%, 100% { 
          opacity: 1;
          transform: scale(1);
        }
        40% { 
          opacity: 0.3;
        }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes pulse {
        0%, 100% { 
          opacity: 1;
          transform: scale(1);
        }
        50% { 
          opacity: 0.6;
          transform: scale(1.05);
        }
      }

      @keyframes bounce {
        0%, 80%, 100% { 
          transform: scale(0);
          opacity: 0.5;
        }
        40% { 
          transform: scale(1);
          opacity: 1;
        }
      }

      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      @keyframes videoGenerationPulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.7;
        }
      }
      
      .generatedImageContainer:hover .generatedImageActions {
        opacity: 1;
      }
      
      .generatedImageContainer:hover .generatedImage {
        opacity: 0.9;
      }
      
      .imageActionButton:hover {
        background-color: white;
        transform: translateY(-1px);
      }
      
      .assistantWorkspaceWebsitePromptButton:hover {
        background-color: ${Colors.purple};
        color: white;
        transform: translateY(-1px);
      }
      
      .assistantWorkspaceWebsiteEditorButton:hover {
        background-color: ${Colors.purple};
        color: white;
      }
      
      .assistantWorkspaceWebsitePublishButton:hover {
        background-color: ${Colors.purpleDark || '#6B46C1'};
        transform: translateY(-1px);
      }

      .quickActionButton:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }

      .quickActionButton.primary:hover {
        background-color: ${Colors.purpleDark || '#6B46C1'};
      }

      .quickActionButton.secondary:hover {
        background-color: ${Colors.purple};
        color: white;
      }

      .videoGenerationCard {
        animation: fadeInUp 0.4s ease-out;
      }

      .videoGenerationProgressFill::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.4),
          transparent
        );
        animation: shimmer 2s infinite;
      }

      .videoGenerationIcon {
        animation: videoGenerationPulse 2s infinite ease-in-out;
      }

      .videoGenerationInlineCompleted:hover .videoGenerationInlinePlayOverlay {
        transform: translate(-50%, -50%) scale(1.1);
        backgroundColor: rgba(0, 0, 0, 0.8);
      }

      .videoGenerationInlineCompleted:hover .videoGenerationInlineVideoThumbnail {
        transform: scale(1.05);
      }

      .videoGenerationInlineSuccessBadge {
        animation: fadeInUp 0.6s ease-out 0.3s both;
      }

      .videoGenerationInlineCard {
        animation: fadeInUp 0.4s ease-out;
      }

      .videoGenerationInlineCompleted {
        animation: fadeInUp 0.6s ease-out;
      }

      .assistantChatMessage:hover .messageDevActions {
        opacity: 1;
      }

      .messageDevActionButton:hover {
        transform: scale(1.1);
      }


    `;
        document.head.appendChild(styleElement);

        // Clean up on unmount
        return () => {
            document.head.removeChild(styleElement);

        };
    }, []);

    // TODO v1.2: come back to this
    useEffect(() => {
        const initializeAssistant = async () => {
            // Check if we have an anonymous session ID in localStorage
            // const brand = await ensureBrandAvailable();
            fetchPlans({
                currency: 'USD',
                useCache: true
            }).then((plans) => {
                console.log('plans', plans)
            })
            // // console.log('brand', brand);
            // if (isAuthenticated) {
            //     // If authenticated user or we already have a brand, fetch data
            //     if (currentBrand?.id) {
            //         fetchJobs();
            //         fetchChats();
            fetchMessageUsage();
            //     }
            // }

        };
        initializeAssistant();
    }, []);

    // Initialize on mount - check for existing session but don't create brand
    useEffect(() => {
        return () => {
            // Clear chat history when user navigates away from the component
            if (isChat && storeMessages.length > 0) {
                clearChatHistory();
            }
        };
    }, [currentBrand?.id, isAuthenticated]);

    // Clear chat history when transitioning from chat mode to non-chat mode
    useEffect(() => {
        if (prevIsChatRef.current && !isChat) {
            // User moved from chat mode to non-chat mode
            clearChatHistory();
        }

        prevIsChatRef.current = isChat;
    }, [isChat, clearChatHistory]);

    // Clear chatId when not in chat mode to ensure fresh chat creation
    useEffect(() => {
        if (!isChat && chatId) {
            setChatId('');
        }
    }, [isChat, chatId]);

    // Effect: Force scroll to bottom when chat is resumed (messages loaded from 0 to many)
    useEffect(() => {
        const currentMessageCount = storeMessages.length
        const prevMessageCount = prevMessageCountRef.current

        // If we went from 0 messages to multiple messages, this indicates chat resumption
        if (prevMessageCount === 0 && currentMessageCount > 0 && isChat) {
            console.log('🔄 Chat resumed with', currentMessageCount, 'messages, forcing scroll to bottom')
            // Force scroll to bottom after a brief delay to ensure DOM has rendered
            setTimeout(() => {
                scrollToBottom(true)
            }, 100)
        }

        prevMessageCountRef.current = currentMessageCount
    }, [storeMessages.length, isChat])

    const handleNewChat = () => {
        clearChatHistory();
        // setIsChat(false);
        setMessageText(''); // Clear the input field as well
        setActiveWorkspace(null); // Clear workspace
        clearQuickActions(); // Clear quick actions
        setChatId(null); // Clear the chat ID to ensure fresh chat creation
        // Navigate back to base assistant page when starting a new chat
        history.push('/');
        setIsChat(false);
    };

    const handleSignUp = () => {
        loginWithRedirect({
            authorizationParams: {
                screen_hint: 'signup'
            }
        });
    };

    const handleLogin = () => {
        loginWithRedirect();
    };



    const hideWorkspace = () => {
        setActiveWorkspace(null);
    };

    // Handle text input with character limit
    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = e.target.value

        if (newText.length <= CHARACTER_LIMIT) {
            setMessageText(newText)
            setIsCharacterLimitReached(false)
        } else {
            // Don't update the text if it exceeds the limit
            setIsCharacterLimitReached(true)
            // Optional: briefly show the warning then hide it
            setTimeout(() => setIsCharacterLimitReached(false), 2000)
        }
    }

    const pushToChatPage = () => {
        if (!chatId || !isChat) {
            const newChatId = generateChatId()
            setChatId(newChatId)
            history.push(`/chats/${newChatId}`)
        }
    }

    // Handle message send
    const handleSendMessage = async (message?: string, hiddenData?: { templateData?: any }) => {
        const messageTextLocal = message || messageText
        if (messageTextLocal.trim()) {

            // Check message limits before sending
            if (!checkMessageLimitAndEnforce('send_message')) {
                return; // User hit their limit, modal will be shown
            }

            // Store the message text before clearing it
            const sentMessageText = messageTextLocal

            // Check if this is a new conversation (no existing chatId) or we're not in chat mode
            if (!chatId || !isChat) {
                pushToChatPage()
            }

            // setIsChat(true)
            setMessageText('')

            // Clear quick actions when starting a new message
            clearQuickActions()

            // Schedule scroll to position user message at top (Claude/ChatGPT style)
            scheduleScrollToUserMessage()

            // Send message to store - this will handle showing the maestro chat
            // and also initiate the streaming response.
            sendMessage(sentMessageText, hiddenData)

            // Clear uploaded assets after sending the message
            clearUploadedAssets()
        }
    }


    const handleTaskClick = async (task: AssistantTask) => {
        if (task.prompt) {
            // Check message limits before executing task
            if (!checkMessageLimitAndEnforce('task_click')) {
                return; // User hit their limit, modal will be shown
            }

            // Check if this task has a chatId - if so, we're continuing an existing conversation
            if (task.chatId) {
                try {
                    // Resume the existing chat
                    await resumeChat(task.chatId);
                    // setIsChat(true);

                    // If the task also has a campaignId, open the campaign approval workspace
                    if (task.campaignId) {
                        setActiveWorkspace({
                            type: 'campaignPreview',
                            data: {
                                campaignId: task.campaignId,
                            }
                        });
                    }

                    // Navigate to the chat URL
                    history.push(`/chats/${task.chatId}`);
                } catch (error) {
                    console.error('Failed to resume chat for task:', error);
                    // Fall back to starting a new conversation if resuming fails
                    setTimeout(() => {
                        handleSendMessage(task.prompt);
                    }, 150);
                }
            } else {
                // Delay sending the message slightly for smoother UX
                setTimeout(() => {
                    handleSendMessage(task.prompt); // Submit automatically using existing handler
                }, 150); // 150ms delay
            }
        } else if (task.shouldLinkToPage) {
            history.push(task.shouldLinkToPage)
        }
    };

    // Upload widget now manages its own state via useUniversalUploadWidget store
    // No need for a success handler since sendMessage will check the store for uploaded assets

    // Handle preview click from UniversalUploadWidget
    const handleUploadPreviewClick = (file: UploadedFile) => {
        setSelectedAssetForLightbox(file);
        setLightboxOpen(true);
    }



    // Handle quick action clicks
    const handleQuickActionClick = (action: QuickAction) => {
        // Check message limits before sending
        if (!checkMessageLimitAndEnforce('quick_action')) {
            return; // User hit their limit, modal will be shown
        }
        try {
            switch (action.actionType) {
                case 'SEND_MESSAGE':

                    // Send message as if user typed it
                    if (action.payload.message) {
                        handleSendMessage(action.payload.message)
                    }
                    break

                case 'OPEN_WORKSPACE':
                    // Open the specified workspace
                    if (action.payload.workspaceType) {
                        setActiveWorkspace({
                            type: action.payload.workspaceType,
                            data: action.payload.data || {}
                        })
                    }
                    break

                case 'OPEN_UPLOAD_ASSETS':
                    // Use the store function to open the upload widget
                    openUniversalUploadWidget([
                        'image/*',
                        'video/*'
                    ])
                    break

                case 'PROMPT':
                    // Pre-fill the input prompt with text
                    if (action.payload.promptText) {
                        setMessageText(action.payload.promptText)
                        // Focus the input field
                        const textarea = document.querySelector('.assistantChatTextarea') as HTMLTextAreaElement
                        if (textarea) {
                            textarea.focus()
                            // Move cursor to end
                            textarea.setSelectionRange(textarea.value.length, textarea.value.length)
                        }
                    }
                    break

                case 'SHOW_MUSIC_SELECTOR':
                    // Show the music selector modal
                    setShowMusicSelector(true)
                    break
                default:
                    console.warn('Unknown quick action type:', action.actionType)
            }

            // Don't clear quick actions here - let them persist until user sends a new message
        } catch (error) {
            console.error('Quick action failed:', error)
            // Could show a toast notification here if available
        }
    }

    // Handle record selection from music selector
    const handleMusicSelectorRecordSelect = (recordDetails: UnifiedSpotifyRecord) => {
        // Close the music selector modal
        setShowMusicSelector(false)

        // Format the message with record details
        const message = `I want to promote this release:\n${recordDetails.name}\n${recordDetails.spotifyUrl}`

        // Send the formatted message
        handleSendMessage(message)
    }

    // Handle test workspace submission
    const handleTestWorkspaceSubmit = () => {
        if (!testCampaignId.trim()) {
            return
        }

        // Close the modal
        setShowTestWorkspaceModal(false)

        // Enter chat mode to show the workspace
        // setIsChat(true)

        // Open the campaign approval workspace with the test campaign ID
        setActiveWorkspace({
            type: 'campaignPreview',
            data: {
                campaignId: testCampaignId.trim(),
            }
        })

        // Clear the input for next time
        setTestCampaignId('')
    }

    // Handle load chat submission
    const handleLoadChatSubmit = async () => {
        if (!testChatId.trim()) {
            return
        }

        // Close the modal
        setShowLoadChatModal(false)

        try {
            // Resume the chat conversation
            await resumeChat(testChatId.trim())
            // setIsChat(true)

            // Navigate to the chat URL
            history.push(`/chats/${testChatId.trim()}`)
        } catch (error) {
            console.error('Failed to load chat conversation:', error)
            // Could show an error message here
        }

        // Clear the input for next time
        setTestChatId('')
    }

    // Demo workspace data generators
    const getDemoWorkspaceData = (workspaceType: string) => {
        switch (workspaceType) {
            case 'templateSelection':
                return {
                    templateCategories: ['popular', 'music'] // Show popular and music categories
                }

            case 'website':
                return {
                    websiteId: 'demo-123',
                    slug: 'demo-artist-site',
                    status: 'DRAFT'
                }

            case 'campaignPreview':
                return {
                    campaignId: 'demo-campaign-456',
                    isDemo: true,
                    // Complete demo campaign object
                    demoCampaign: {
                        id: 'demo-campaign-456',
                        name: 'Summer Music Promotion Campaign',
                        playbook_type: 'INSTAGRAM_MUSIC_PROMOTION',
                        status: 'READY_FOR_SUBMISSION',
                        goal_link: 'https://open.spotify.com/track/demo-midnight-dreams',
                        agent_notes: {
                            strategy_summary: 'Targeted Instagram and Facebook campaign to promote the new single "Midnight Dreams" to indie music lovers aged 18-34.',
                            one_liner: 'Dreamy indie vibes for late-night music discovery',
                            targeted_audiences: ['Indie Music Fans', 'Alternative Rock Listeners', 'Music Discovery', 'Concert Goers'],
                            targeted_locations: ['United States', 'Canada', 'United Kingdom', 'Australia'],
                            estimation_expectations: 'Expecting 15K-25K reach with 3-5% engagement rate among indie music fans',
                            budget_guidance: 'Optimal budget for 2-week campaign with strong performance potential'
                        },
                        campaign_input: {
                            playbookType: 'INSTAGRAM_MUSIC_PROMOTION' as any,
                            campaignName: 'Midnight Dreams - Music Promotion',
                            goalLink: 'https://open.spotify.com/track/demo-midnight-dreams',
                            platform: 'meta' as any,
                            budget: 2500,
                            budgetType: 'lifetime' as const,
                            length: {
                                startDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
                                endDate: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString()
                            },
                            creatives: [
                                {
                                    source: {
                                        type: 'uploaded_asset' as const,
                                        mediaUrl: 'https://res.cloudinary.com/symphony-images/video/upload/v1699901234/demo_music_video.mp4',
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_music_thumbnail.jpg',
                                        cloudinaryId: 'demo_music_video',
                                        assetType: 'video' as const,
                                        wasAiGenerated: false
                                    },
                                    caption: '🌙 Dive into the dreamy soundscape of "Midnight Dreams" ✨ Our latest single captures those late-night moments when the city sleeps but your heart is wide awake. Stream now! 🎵 #MidnightDreams #IndieMusic #NewMusic',
                                    cta: 'LISTEN_MUSIC' as any
                                }
                            ]
                        },
                        readyForSubmission: true
                    }
                }

            case 'marketingPlan':
                return {
                    planId: 'demo-plan-789',
                    name: 'Album Launch Strategy',
                    status: 'draft',
                    type: 'launch'
                }

            case 'facebookConnect':
                return {
                    reason: 'Connect your Facebook account to run ads',
                    nextStep: 'facebook_login',
                    skipOption: true
                }

            case 'instagramPostSelection':
                return {
                    campaignId: 'demo-instagram-campaign-123'
                }

            case 'contentAnalysis':
                return {
                    analysisId: 'demo-analysis-321',
                    isDemo: true,
                    // Demo analysis data to prevent API call
                    demoAnalysisResult: {
                        success: true,
                        analysis: {
                            analyzedPosts: [
                                {
                                    postMetadata: {
                                        caption: 'Sunset vibes and dreamy melodies 🌅✨ Our latest track captures those golden hour moments perfectly. What do you think? 🎵',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-1',
                                        engagement: 2847,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_1.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Music', 'Sunset', 'Emotions', 'Indie Vibes'],
                                            video_narrative: 'Artist sharing intimate moment with music during golden hour',
                                            visual_elements: ['Warm lighting', 'Natural setting', 'Authentic expression', 'Musical instruments']
                                        },
                                        detailed_summary: 'High-performing post showcasing authentic musical moment with strong emotional connection. Uses natural lighting and relatable content to engage indie music audience.',
                                        target_audience_analysis: {
                                            demographics: ['18-34 years old', 'Music lovers', 'Urban and suburban'],
                                            psychographics: ['Emotionally-driven', 'Values authenticity', 'Seeks musical discovery'],
                                            public_figures_brands: ['Indie artists', 'Music discovery platforms'],
                                            topics: ['New music', 'Artist lifestyle', 'Creative process', 'Emotional expression']
                                        }
                                    }
                                },
                                {
                                    postMetadata: {
                                        caption: 'Studio sessions hit different when you\'re in your zone 🎧🔥 Working on something special that I can\'t wait to share with you all. The creative process is everything! #StudioLife #NewMusic #CreativeProcess',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-2',
                                        engagement: 3521,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_2.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Music Production', 'Behind-the-scenes', 'Creative Process', 'Studio Life'],
                                            video_narrative: 'Artist in studio environment showing authentic creative process',
                                            visual_elements: ['Professional studio setup', 'Recording equipment', 'Focused expression', 'Low lighting']
                                        },
                                        detailed_summary: 'Top-performing behind-the-scenes content that builds anticipation while showing authentic creative process. Engages fans with exclusive studio access.',
                                        target_audience_analysis: {
                                            demographics: ['Music enthusiasts', '20-35 years old', 'Creative professionals'],
                                            psychographics: ['Values craftsmanship', 'Appreciates creative process', 'Seeks exclusivity'],
                                            public_figures_brands: ['Music producers', 'Audio equipment brands', 'Recording studios'],
                                            topics: ['Music production', 'Artist journey', 'Creative inspiration', 'Professional development']
                                        }
                                    }
                                },
                                {
                                    postMetadata: {
                                        caption: 'Late night city drives with this playlist on repeat 🌃✨ Sometimes the best inspiration comes when the world is quiet. What songs get you through those reflective moments? Drop your recommendations below 👇',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-3',
                                        engagement: 1893,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_3.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Urban Life', 'Night Vibes', 'Personal Reflection', 'Community Engagement'],
                                            video_narrative: 'Artist sharing personal moment and inviting community interaction',
                                            visual_elements: ['City lights', 'Car interior', 'Night atmosphere', 'Urban aesthetic']
                                        },
                                        detailed_summary: 'Community-building post that combines personal storytelling with audience engagement. Creates connection through shared experiences and music discovery.',
                                        target_audience_analysis: {
                                            demographics: ['Urban millennials', '22-30 years old', 'Night owls'],
                                            psychographics: ['Introspective', 'Values community', 'Seeks connection'],
                                            public_figures_brands: ['Lifestyle brands', 'Music streaming services', 'Urban culture influencers'],
                                            topics: ['Music discovery', 'Personal moments', 'City life', 'Community building']
                                        }
                                    }
                                },
                                {
                                    postMetadata: {
                                        caption: 'Throwback to our first live performance 🎤💫 From nervous energy to pure magic on stage. Every artist\'s journey starts somewhere - what was your first big moment? #ThrowbackThursday #LiveMusic #ArtistJourney',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-4',
                                        engagement: 4182,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_4.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Live Performance', 'Artist Journey', 'Nostalgia', 'Personal Growth'],
                                            video_narrative: 'Artist reflecting on career milestone and connecting with audience experiences',
                                            visual_elements: ['Stage lighting', 'Performance energy', 'Crowd interaction', 'Vintage aesthetic']
                                        },
                                        detailed_summary: 'Highly engaging nostalgic content that humanizes the artist and encourages audience sharing. Builds community through shared milestone experiences.',
                                        target_audience_analysis: {
                                            demographics: ['Music fans', '18-40 years old', 'Live music enthusiasts'],
                                            psychographics: ['Nostalgic', 'Values authenticity', 'Appreciates growth stories'],
                                            public_figures_brands: ['Live music venues', 'Music festivals', 'Other emerging artists'],
                                            topics: ['Career milestones', 'Personal growth', 'Live music', 'Artist development']
                                        }
                                    }
                                },
                                {
                                    postMetadata: {
                                        caption: 'Collaborating with @indie_producer_sam on something that\'s going to blow your minds 🤯🎵 When two creative energies align, magic happens. Can\'t reveal too much yet but... stay tuned 👀 #Collaboration #ComingSoon #IndieMusic',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-5',
                                        engagement: 2756,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_5.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Collaboration', 'Anticipation Building', 'Creative Partnership', 'Industry Connections'],
                                            video_narrative: 'Artist teasing upcoming collaboration and building anticipation',
                                            visual_elements: ['Dual creative presence', 'Professional setup', 'Dynamic interaction', 'Mysterious preview']
                                        },
                                        detailed_summary: 'Strategic anticipation-building content that leverages collaboration for expanded reach. Creates buzz while showcasing professional network.',
                                        target_audience_analysis: {
                                            demographics: ['Music industry professionals', '20-35 years old', 'Collaboration seekers'],
                                            psychographics: ['Values innovation', 'Seeks exclusive content', 'Appreciates artistic partnerships'],
                                            public_figures_brands: ['Music producers', 'Collaborative platforms', 'Industry networks'],
                                            topics: ['Music collaboration', 'Creative partnerships', 'Industry networking', 'Exclusive previews']
                                        }
                                    }
                                },
                                {
                                    postMetadata: {
                                        caption: 'Coffee, vinyl records, and Sunday morning songwriting sessions ☕️🎶 There\'s something about analog sounds that just hits different. What\'s your favorite way to disconnect and create? #SundayVibes #VinylCollection #Songwriting',
                                        username: 'luna_rose_music',
                                        timestamp: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
                                        permalink: 'https://instagram.com/p/demo-post-6',
                                        engagement: 1647,
                                        thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_post_6.jpg'
                                    },
                                    analysis: {
                                        content_breakdown: {
                                            themes_topics: ['Lifestyle', 'Creative Ritual', 'Analog Culture', 'Mindful Creation'],
                                            video_narrative: 'Artist sharing intimate creative ritual and lifestyle aesthetic',
                                            visual_elements: ['Cozy interior', 'Vintage vinyl', 'Morning light', 'Contemplative mood']
                                        },
                                        detailed_summary: 'Lifestyle content that builds personal brand through creative ritual sharing. Appeals to vinyl culture and mindful creation audiences.',
                                        target_audience_analysis: {
                                            demographics: ['Vinyl enthusiasts', '25-40 years old', 'Coffee culture participants'],
                                            psychographics: ['Values authenticity', 'Appreciates analog culture', 'Seeks mindful experiences'],
                                            public_figures_brands: ['Coffee brands', 'Vinyl record stores', 'Lifestyle brands'],
                                            topics: ['Creative rituals', 'Analog culture', 'Mindful living', 'Personal aesthetics']
                                        }
                                    }
                                }
                            ],
                            recommendations: {
                                contentThemes: ['Authentic moments', 'Behind-the-scenes', 'Golden hour aesthetics', 'Emotional storytelling'],
                                visualElements: ['Natural lighting', 'Warm color palettes', 'Intimate settings', 'Musical instruments'],
                                contentPatterns: ['Personal stories', 'Process glimpses', 'Fan engagement', 'Lifestyle content']
                            }
                        }
                    }
                }

            default:
                return {}
        }
    }

    const handleDemoWorkspaceOpen = (workspaceType: string) => {
        const demoData = getDemoWorkspaceData(workspaceType)

        // Close the modal
        setShowDemoWorkspaceModal(false)

        // Enter chat mode to show the workspace
        // setIsChat(true)

        // Open the selected workspace with demo data
        setActiveWorkspace({
            type: workspaceType as any,
            data: demoData
        })
    }

    const handleAddMessage = () => {
        if (newMessageText.trim()) {
            addCustomMessage(newMessageText.trim(), newMessageIsUser)
            setNewMessageText('')
            setShowAddMessageModal(false)
        }
    }

    // Handle job/task clicks
    const handleJobClick = async (job: AgentJob) => {
        try {
            console.log('job', job)

            // Handle different job types with specific navigation logic
            if (job.type === 'campaign') {
                // For campaign jobs, navigate directly to the marketing campaign details page
                history.push(`/marketing/campaigns/${job.id}`)
                return // Exit early for campaign jobs
            }

            // For non-campaign jobs, continue with the existing chat/workspace logic
            // Always ensure we're in chat mode
            // setIsChat(true)

            // If the job has a chat, resume that chat first
            if (job.chatId) {
                await resumeChat(job.chatId)
                history.push(`/chats/${job.chatId}`)
            } else {
                // No chat available, generate a new chat ID and navigate to it
                const newChatId = generateChatId()
                setChatId(newChatId)
                history.push(`/chats/${newChatId}`)
            }

            // Handle different non-campaign job types by opening appropriate workspaces
            if (job.type === 'content_analysis') {
                // For content analysis jobs, open the contentAnalysis workspace
                if (job.analysisId) {
                    setActiveWorkspace({
                        type: 'contentAnalysis',
                        data: {
                            analysisId: job.analysisId,
                            isLoading: false
                        }
                    })
                    // Don't show the details modal since we're opening the workspace
                    setShowJobDetailsModal(false)
                } else {
                    console.error('Content analysis job missing analysisId:', job)
                    // Fallback to modal if no analysisId
                    await fetchJobDetails(job.id, job.type)
                }
            } else if (job.type === 'marketing_plan') {
                // Open the marketing plan workspace
                setActiveWorkspace({
                    type: 'marketingPlan',
                    data: {
                        planId: String(job.id),
                        isLoading: false
                    }
                })
                // Don't show the details modal since we're opening the workspace
                setShowJobDetailsModal(false)
            } else if (job.type === 'asset') {
                // For asset jobs, show the details modal
                await fetchJobDetails(job.id, job.type)
            }
        } catch (error) {
            console.error('Failed to handle job click:', error)
            // Fallback to just opening the details modal for any job type
            await fetchJobDetails(job.id, job.type)
        }
    }

    // Handle chat clicks to resume
    const handleChatClick = async (chat: ChatConversation) => {
        try {
            await resumeChat(chat.id)
            // setIsChat(true)
            // Navigate to the chat URL
            history.push(`/chats/${chat.id}`)
        } catch (error) {
            console.error('Failed to resume chat:', error)
        }
    }

    // Render a single task/job item
    const renderJobItem = (job: AgentJob) => {
        const getStatusColor = (status: string) => {
            switch (status.toLowerCase()) {
                case 'completed': return Colors.green
                case 'generating': case 'executing': return Colors.purple
                case 'failed': return Colors.red
                default: return Colors.greyDark
            }
        }

        const getJobIcon = (type: string, metadata: any, job?: any) => {
            if (type === 'campaign') return '🚀'
            if (type === 'asset') return metadata.assetType === 'video' ? '🎬' : '🎨'
            if (type === 'content_analysis') {
                // Use platform-specific icons
                const platform = job?.platform?.toLowerCase()
                if (platform === 'instagram') return '📸'
                if (platform === 'tiktok') return '🎵'
                return '📊'
            }
            return '📋'
        }

        return (
            <div
                key={job.id}
                className="jobItem"
                style={sty.jobItem}
                onClick={() => handleJobClick(job)}
            >
                <div className="jobItemContent" style={sty.jobItemContent}>
                    <div className="jobItemLeft" style={sty.jobItemLeft}>
                        <span className="jobItemIcon" style={sty.jobItemIcon}>
                            {getJobIcon(job.type, job.metadata, job)}
                        </span>
                        <div className="jobItemInfo" style={sty.jobItemInfo}>
                            <h3 className="jobItemTitle" style={sty.jobItemTitle}>{job.title}</h3>
                            <p className="jobItemDescription" style={sty.jobItemDescription}>
                                {job.description}
                            </p>
                        </div>
                    </div>
                    <div className="jobItemRight" style={sty.jobItemRight}>
                        <div
                            className="jobItemStatus"
                            style={styx(sty.jobItemStatus, { backgroundColor: getStatusColor(job.status) + '20', color: getStatusColor(job.status) })}
                        >
                            {job.status}
                        </div>
                        {job.progress > 0 && (
                            <div className="jobItemProgressContainer" style={sty.jobItemProgressContainer}>
                                <div
                                    className="jobItemProgress"
                                    style={styx(
                                        sty.jobItemProgress,
                                        { backgroundColor: getStatusColor(job.status), width: `${job.progress}%` }
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="jobItemFooter" style={sty.jobItemFooter}>
                    <span className="jobItemDate" style={sty.jobItemDate}>
                        {new Date(job.updatedAt).toLocaleString()}
                    </span>
                    {job.chatId && (
                        <span className="jobItemChatLink" style={sty.jobItemChatLink}>
                            💬 Has chat
                        </span>
                    )}
                </div>
            </div>
        )
    }

    // Render a tab section (chats or jobs)
    const renderTabSection = (type: 'chats' | 'jobs') => {
        const isChats = type === 'chats';
        const isLoading = isChats ? isLoadingChats : isLoadingTasks;
        const items = isChats ? chats : agentJobs;
        const tabId = isChats ? 'chats' : 'jobs';
        const loadingText = isChats ? 'Loading chats...' : 'Loading jobs...';
        const emptyIcon = isChats ? 'chat' : 'briefcase';
        const emptyTitle = isChats ? 'No previous chats' : 'No jobs yet';
        const emptyDescription = isChats
            ? 'Start a new conversation to see your chat history here!'
            : 'Create campaigns or generate assets to see your jobs here!';
        const renderItem = isChats ? renderChatItem : renderJobItem;

        return (
            <TabSymphony
                className={`${type}Tab`}
                visible={activeTab.id === tabId}
            >
                <div className={`${type}TabContent`} style={sty.tabContentScrollable}>
                    {isLoading ? (
                        <div className="loadingState" style={sty.loadingState}>
                            <Icon className="loadingIcon" icon="maestroBadgeImage" size={24} style={sty.loadingIcon} />
                            <span>{loadingText}</span>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="emptyState" style={sty.emptyState}>
                            <Icon className="emptyIcon" icon={emptyIcon} size={48} style={sty.emptyIcon} />
                            <h3 style={sty.emptyTitle}>{emptyTitle}</h3>
                            <p style={sty.emptyDescription}>{emptyDescription}</p>
                        </div>
                    ) : (
                        <div className={`${type}List`} style={sty.itemsList}>
                            {items.map((item: any) => renderItem(item))}
                        </div>
                    )}
                </div>
            </TabSymphony>
        );
    };

    // Render a single chat item
    const renderChatItem = (chat: ChatConversation) => {
        // Format time without seconds
        const formatTime = (date: Date) => {
            return date.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        };

        return (
            <div
                key={chat.id}
                className="chatItem"
                style={sty.chatItem}
                onClick={() => handleChatClick(chat)}
            >
                <div className="chatItemContent" style={sty.chatItemContent}>
                    <div className="chatItemInfo" style={sty.chatItemInfo}>
                        <h3 className="chatItemTitle" style={sty.chatItemTitle}>
                            {chat.title || `${chat.conversationType.replace('_', ' ').toUpperCase()} Chat`}
                        </h3>
                        <p className="chatItemDescription" style={sty.chatItemDescription}>
                            {chat.messageCount} messages
                        </p>
                    </div>
                </div>
                <div className="chatItemFooter" style={sty.chatItemFooter}>
                    <span className="chatItemDate" style={sty.chatItemDate}>
                        Last active: {formatTime(new Date(chat.lastActiveAt))}
                    </span>
                    <span className="chatItemStarted" style={sty.chatItemStarted}>
                        Started: {new Date(chat.startedAt).toLocaleDateString()}
                    </span>
                </div>
            </div>
        )
    }

    /** Function to render markdown with workspace buttons */
    const renderMarkdownContent = (content: string) => {
        // Regular markdown rendering with workspace buttons
        return (
            <div className="markdownContentWithButtons">
                <AssistantMarkdownParser
                    content={content} />
            </div>
        )
    }

    const renderAIMessageContent = (text: string, isStreaming: boolean = false) => {
        if (isStreaming) {
            return (
                <div className="streamingTextContainer"
                    style={styx(sty.streamingTextContainer)}>
                    {renderMarkdownContent(text)}
                </div>
            )
        } else {
            return renderMarkdownContent(text)
        }
    }

    // Render a single message
    const renderMessage = (message: MessageItem) => {
        const {
            isUser,
            text,
            media,
            summaryCard,
            videoGeneration,
            assetGeneration,
            systemMessages,
            isStreaming,
            isLoadingMessage,
            parts
        } = message

        const hasMedia = media && (media.type === 'image' || media.type === 'video')

        const renderMessageContent = () => {
            const messageParts = parts || [];

            // If we have parts, use parts-based rendering
            if (messageParts && messageParts.length > 0) {
                return (
                    <div className="message-parts-container">
                        {messageParts.map((part: MessagePart) => {
                            switch (part.type) {
                                case 'text':
                                    // Only render if there's content
                                    return part.content ? (
                                        <div key={part.id} className="text-part">
                                            {renderAIMessageContent(part.content, isStreaming)}
                                        </div>
                                    ) : null;

                                case 'tool_call':
                                    return <ToolCallIndicator key={part.id} toolCall={part} />;

                                default:
                                    return null;
                            }
                        })}
                    </div>
                );
            }

            // Fallback to text-based rendering for backward compatibility
            if (text) {
                return renderAIMessageContent(text, isStreaming);
            }
            return null;
        };


        return (
            <div
                className={clsx('assistantChatMessage', isUser ? 'userMessage' : 'assistantMessage')}
                style={styx(
                    mStyle(sty, "message"),
                    isUser ? styx(sty.userMessage) : styx(
                        sty.assistantMessage,
                        mobileView ? { padding: '0 8px' } : {}
                    )
                )}
            >
                {!isUser && renderMaestroIcon(isLoadingMessage || false)}
                {!isUser && isLoadingMessage && renderLoadingDots()}

                <div className="assistantChatMessageContent" style={styx(sty.messageContent, isUser && {
                    marginTop: 0,
                    marginBottom: 0,
                    paddingRight: 0,
                }, !isUser && {
                    color: 'white'
                })}>
                    {/* Message Actions - Available in both dev and demo mode */}
                    {!isStreaming && (devMode || demoMode) && (
                        <MessageActions
                            message={message}
                            showDevModeFeatures={devMode}
                            className="messageActions"
                            onAddMessage={() => setShowAddMessageModal(true)}
                            isUserMessage={isUser}
                        />
                    )}

                    {/* Render system messages at the top of the assistant message - dev mode only */}
                    {devMode && !isUser &&
                        <SystemMessages messages={systemMessages || []} />
                    }

                    {/* Render thinking section for streaming messages - dev mode only */}
                    {devMode && isStreaming && !isUser && (
                        <ThinkingSection
                            thinkingState={thinkingState}
                            initiallyOpen={thinkingProcessInitiallyOpen}
                        />
                    )}



                    {/* Render content based on message state */}
                    {/* {text && (
                        renderAIMessageContent(text, isStreaming)
                    )} */}
                    {renderMessageContent()}

                    {/* Uploaded assets for user messages */}
                    {message.uploadedAssets && message.uploadedAssets.length > 0 && (
                        <MessageUploadedAssets
                            uploadedAssets={message.uploadedAssets}
                            className="messageUploadedAssets"
                        />
                    )}

                    {/* Open Workspace button for messages with workspace tool calls */}
                    {message.metadata?.tool_calls?.some(tc => tc.name === 'openWorkspace') && (
                        <div style={{ marginTop: 12 }}>
                            {(() => {
                                const workspaceToolCall = message.metadata.tool_calls.find(tc => tc.name === 'openWorkspace');
                                if (!workspaceToolCall) return null;

                                try {
                                    const workspaceData = JSON.parse(workspaceToolCall.arguments);
                                    const workspaceInfo = WORKSPACE_BUTTON_MAPPING[workspaceData.workspaceType] || {
                                        title: 'Open Workspace',
                                        icon: 'window'
                                    };

                                    return (
                                        <ButtonSymphony
                                            className="openWorkspaceButton"
                                            variant="outlined"
                                            color="purple"
                                            onClick={() => {
                                                setActiveWorkspace({
                                                    type: workspaceData.workspaceType,
                                                    data: workspaceData.data || {}
                                                });
                                            }}
                                            iconLeft={<Icon className="openWorkspaceIcon" icon={workspaceInfo.icon} size={16} />}
                                            style={{
                                                marginBottom: 8,
                                                borderColor: Colors.purpleLight,
                                                color: Colors.purpleLight
                                            }}
                                        >
                                            {workspaceInfo.title}
                                        </ButtonSymphony>
                                    );
                                } catch (e) {
                                    console.error('Failed to parse workspace data:', e);
                                    return null;
                                }
                            })()}
                        </div>
                    )}

                    {/* Render unified asset generation inline component */}
                    {assetGeneration && (
                        <div style={{ marginTop: 12 }}>
                            <AssetGenerationInline assetGeneration={assetGeneration} />
                        </div>
                    )}

                    {/* Render summary card at the bottom of the message */}
                    {summaryCard && (
                        <div style={{ marginTop: 12 }}>
                            <AssistantSummaryCard summaryCard={summaryCard} />
                        </div>
                    )}


                </div>
            </div>
        )
    }

    // --- START Lightbox Modal Renderer ---
    const renderLightboxModal = () => {
        if (!lightboxOpen || !selectedAssetForLightbox) {
            return null;
        }

        const asset = selectedAssetForLightbox;
        const isImage = asset.iconType === 'image';
        const isVideo = asset.iconType === 'video';
        const fileUrl = asset.s3Url || (asset.file ? URL.createObjectURL(asset.file) : undefined);

        return (
            <ModalSymphony
                className="lightboxModal"
                isOpen={lightboxOpen}
                onClose={() => {
                    setLightboxOpen(false);
                    setSelectedAssetForLightbox(null);
                }}
                title={asset.name}
                size="medium" // Use a larger size for media
            >
                {isImage && fileUrl && (
                    <img src={fileUrl} alt={asset.name} style={sty.lightboxImage} />
                )}
                {isVideo && fileUrl && (
                    <video src={fileUrl} controls style={sty.lightboxVideo}>
                        Your browser does not support the video tag.
                    </video>
                )}
                {!(isImage || isVideo) && (
                    <div style={sty.lightboxGenericContent}>
                        <Icon
                            className="uploadedAssetIcon"
                            icon="file" size={64} color={Colors.greyDark} />
                        <p>Preview not available for this file type.</p>
                        <p>Filename: {asset.name}</p>
                        {fileUrl && (
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={sty.downloadLink}>
                                Download File
                            </a>
                        )}
                    </div>
                )}
            </ModalSymphony>
        );
    };
    // --- END Lightbox Modal Renderer ---

    // Render the top navigation bar for chat mode
    const renderChatTopBar = () => {
        if (!isChat) {
            return null;
        }

        return (
            <div className="assistantChatTopBar" style={styx(mStyle(sty, "assistantChatTopBar"))}>
                <div className="assistantChatTopBarLeft" style={sty.assistantChatTopBarLeft}>
                    <ButtonSymphony
                        className="assistantChatBackButton"
                        style={sty.assistantChatBackButton}
                        onClick={handleNewChat}
                        type="button"
                    >
                        <Icon
                            className="assistantChatBackButtonIcon"
                            icon="sLogoPurple"
                            size={32}
                        />
                    </ButtonSymphony>
                </div>

                {renderDevModeToggle()}

                <div className="assistantChatTopBarRight" style={sty.assistantChatTopBarRight}>
                    {isAuthenticated ? (
                        <ButtonSymphony
                            className="assistantChatNewChatButton"
                            style={sty.assistantChatNewChatButton}
                            onClick={handleNewChat}
                            disabled={isUploading}
                            variant="outlined"
                            color="purple"
                        >
                            New Chat
                        </ButtonSymphony>
                    ) : (
                        <>
                            <ButtonSymphony
                                className="assistantChatGetStartedButton"
                                style={sty.assistantChatGetStartedButton}
                                onClick={handleSignUp}
                                variant="primary"
                                size="medium"
                            >
                                Get Started
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="assistantChatLoginButton"
                                style={sty.assistantChatLoginButton}
                                onClick={handleLogin}
                                variant="outlined"
                                color="purple"
                                size="medium"
                            >
                                Log In
                            </ButtonSymphony>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderMaestroChangeLog = (): JSX.Element => {

        const changeLogsUpdates = [
            {
                title: 'v1.1: Released July 16, 2025',
                subtitle: 'Latest updates and improvements',
                features: [
                    '💬 Switched from "Tasks" to clearer "Message Limits" pricing',
                    '🗂️ Upload files and save them for future use (beta)',
                    '🧠 Built-in memory with ability to search past chats',
                    '📊 Analyze top Instagram posts (when logged in via Instagram)',
                    '🔧 Fixed upload functionality for campaign creation',
                    '🔄 Chats now automatically have titles',
                    '✅ Fixed campaign submission issues (AWAITING_AGENT_CAMPAIGN status)'
                ]
            },
            {
                title: 'Pricing Updates',
                subtitle: 'New plan features and capabilities',
                features: [
                    '🚀 Growth Plan: Now includes marketing plan + content analysis tools',
                    '✨ Starter Plan: Campaign creation + campaign analysis',
                    '🔜 Coming Soon: Website + pre-save creation via chat (Starter Plan)'
                ]
            },
            {
                title: 'v1.0: Initial Launch',
                subtitle: 'Core marketing capabilities',
                features: [
                    '🚀 Create TikTok + Instagram campaigns',
                    '📋 Generate comprehensive marketing plans',
                    '📈 Analyze content performance and insights'
                ]
            }
        ]
        return (
            <>
                <button
                    className='maestroChangeLog'
                    onClick={() => setShowMaestroChangeLogModal(true)}
                    style={styx(
                        sty.assistantChatActionButton,
                        mStyle(sty, "assistantChatActionButton")
                    )}
                    type="button"
                    title="What's new in v1.1"
                >
                    <span style={{ fontSize: 12, fontWeight: 400, color: Colors.purpleLight }}>v1.1</span>
                </button>
                <ModalSymphony
                    className='maestroChangeLogModal'
                    isOpen={showMaestroChangeLogModal}
                    onClose={() => setShowMaestroChangeLogModal(false)}
                    title='Maestro Change Log'
                    subtitle='What’s new in Maestro v1.1'

                >
                    <div className='maestroChangeLogModalContent' style={{ padding: '20px 0' }}>
                        {changeLogsUpdates.map((update, index) => (
                            <div key={index} className='maestroChangeLogModalContentSection' style={{ marginBottom: 32 }}>
                                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{update.title}</h2>
                                <p style={{ fontSize: 14, color: Colors.greyDark, marginBottom: 16 }}>{update.subtitle}</p>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {update.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} style={{
                                            fontSize: 14,
                                            marginBottom: 8,
                                            paddingLeft: 8,
                                            lineHeight: 1.5
                                        }}>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </ModalSymphony>
            </>
        )
    }

    // Render the chat input area
    const renderInputArea = () => {
        return (
            <div
                {...getRootProps({
                    className: clsx("assistantChatInputContainer", isDragActive && "dragActive", isDragAccept && "dragAccept", isDragReject && "dragReject")
                })}
                style={styx(
                    mStyle(
                        sty,
                        "assistantChatInputContainer",
                    ),
                    isChat && mStyle(sty, "assistantChatInputContainer_chatMode"),
                    isDragActive && sty.dropzoneActive
                )}
            >
                {renderFeedbackPrompt()}
                {renderUploadFilePreview()}

                <input {...getInputProps()} />

                <textarea
                    className="assistantChatTextarea"
                    style={mStyle(sty, "assistantChatInput")}
                    value={messageText}
                    onChange={handleTextareaChange}
                    placeholder={isChat ? "Reply to Maestro" : "Ask anything or paste a link"}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isLoadingAIResponse && !isStreaming) {
                            e.preventDefault()
                            handleSendMessage()
                        }
                    }}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                />

                {/* Character limit indicator */}
                {isCharacterLimitReached && (
                    <div style={sty.characterLimitIndicator}>
                        Character limit reached ({CHARACTER_LIMIT})
                    </div>
                )}

                <div className='assistantChatInputActions' style={sty.assistantChatInputActions}>
                    <div className="assistantChatLeftActions" style={sty.assistantChatLeftActions}>
                        {/* Upload Assets Button with Dropdown - Left */}
                        <UploadButtonWithDropdown
                            className="assistantChatUploadButton"
                            disabled={isLoadingAIResponse || isStreaming || isUploading}
                            buttonStyle={mStyle(sty, "assistantChatActionButton")}
                        />
                        {renderMaestroChangeLog()}
                    </div>


                    {/* Task Counter - Center */}
                    {/* <MaestroTaskCounter
                        className="assistantChatTaskCounter"
                        showUpgradeHint={true}
                    /> */}
                    <MaestroMessageLimitCounter
                        className="assistantChatMessageLimitCounter"
                        showUpgradeHint={true}
                    />

                    {/* Send Button - Right */}
                    <button
                        className="assistantChatSendButton"
                        style={styx(
                            sty.assistantChatSendButton,
                            (isLoadingAIResponse || isStreaming || isUploading) ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                        )}
                        onClick={() => handleSendMessage()}
                        disabled={isLoadingAIResponse || isStreaming || isUploading}
                        type="button"
                    >
                        <Icon
                            className="assistantChatSendIcon"
                            icon="send"
                            size={20}
                            color="white"
                        />
                    </button>
                </div>

                {/* Universal Upload Widget - Only renders the input */}
                <UniversalUploadWidget
                    chatId={chatId || undefined}
                    maxFiles={5}
                    allowMultiple={true}
                    className="assistantChatUploadWidget"
                    externalFiles={externalFilesToUpload}
                />
            </div >
        );
    }

    const renderDevModeToggle = () => {
        if (isAuthenticated && isSymphonyAdmin) {
            return (
                <div className="questionDevModeToggle" style={sty.questionDevModeToggle}>
                    <div className="devModeToggle" style={sty.devModeToggle}>
                        <span style={styx(sty.devModeLabel)}>Dev Mode</span>
                        <button
                            className="devModeToggleButton"
                            style={styx(
                                sty.devModeToggleButton,
                                devMode ? sty.devModeToggleButton_active : sty.devModeToggleButton_inactive
                            )}
                            onClick={() => setDevMode(!devMode)}
                            type="button"
                        >
                            <div
                                className="devModeToggleSlider"
                                style={styx(
                                    sty.devModeToggleSlider,
                                    devMode ? sty.devModeToggleSlider_active : sty.devModeToggleSlider_inactive
                                )}
                            />
                        </button>
                    </div>
                </div>
            )
        }
    }

    const renderTabs = () => {
        // Check for anonymous session to show tabs for returning users
        const anonymousSessionId = getAnonymousSessionIdFromLocalStorage();

        // Show tabs if:
        // 1. User is authenticated OR
        // 2. Has a current brand OR  
        // 3. Has an anonymous session (returning user)
        const shouldShowTabs = isAuthenticated;

        return (
            <div style={mStyle(sty, "assistantChatTabsContainer")}>
                {shouldShowTabs && (<TabsSymphony
                    isDarkBackground={true}
                    className="maestroTabs"
                    tabs={MAESTRO_TABS}
                    selectedTab={activeTab}
                    onChange={(tab) => setActiveTab(tab as TabItem)}
                />)}

                {/* Tab Content Container with fixed height */}
                <div className="tabContentContainer" style={sty.tabContentContainer}>
                    <TabSymphony
                        className="tasksTab"
                        visible={activeTab.id === 'recommendations'}
                    >
                        <div className="tasksTabContent" style={sty.tabContentScrollable}>
                            {/* Dev Mode Tools */}
                            {devMode && <div className="devModeToolsContainer" style={sty.devModeToolsContainer}>
                                <ButtonSymphony
                                    className="testWorkspaceButton"
                                    style={sty.devModeToolButton}
                                    onClick={() => setShowTestWorkspaceModal(true)}
                                    iconLeft="gear"
                                    variant="outlined"
                                >
                                    View Test Workspace
                                </ButtonSymphony>

                                <ButtonSymphony
                                    className="loadChatButton"
                                    style={sty.devModeToolButton}
                                    onClick={() => setShowLoadChatModal(true)}
                                    iconLeft="message"
                                    variant="outlined"
                                >
                                    Load Chat Conversation
                                </ButtonSymphony>

                                <ButtonSymphony
                                    className="demoWorkspaceButton"
                                    style={sty.devModeToolButton}
                                    onClick={() => setShowDemoWorkspaceModal(true)}
                                    iconLeft="search"
                                    variant="outlined"
                                >
                                    Demo Workspaces
                                </ButtonSymphony>
                            </div>}

                            <DailyTasks
                                onTaskClick={handleTaskClick}
                            />
                        </div>
                    </TabSymphony>

                    {renderTabSection('chats')}
                    {renderTabSection('jobs')}
                </div>
            </div>
        )
    }

    const renderDisclaimer = () => {
        return (
            <div style={sty.disclaimer}>Maestro is in beta and may make mistakes. Please double-check responses.</div>
        )
    }

    // Render the initial question view with tabs
    const renderQuestion = () => {
        let titleToRender = "How can I help you grow?"
        let subtitleToRender: string | null = "Meet Maestro, by SymphonyOS. An AI marketing agency in your pocket.\nRun ads, grow your content, and more."

        if (isAuthenticated) {
            if (isFirstSession) {
                titleToRender = "Welcome to SymphonyOS!"
                subtitleToRender = "Your AI-powered marketing agency in your pocket. Get started by taking our first actions."
            } else if (isAuthenticated) {
                subtitleToRender = null
            }
        } else {
        }

        if (title) {
            titleToRender = title
            subtitleToRender = null
        }

        return (
            <>
                <div
                    className="assistantChatQuestion"
                    style={mStyle(sty, "assistantChatQuestion")}
                >
                    {renderDevModeToggle()}

                    <Icon
                        className="assistantChatHeaderIcon"
                        icon="maestroBadgeImage"
                        size={62}
                    />
                    <div
                        className="assistantChatTitle"
                        style={styx(Sty.text24bold, mStyle(sty, "assistantChatTitle"))}
                    >
                        {titleToRender}
                        {subtitleToRender && <p style={styx(mStyle(sty, "assistantChatSubtitle"))}>{subtitleToRender}</p>}
                    </div>
                </div >

                {/* Text Input Area */}
                {/* Text Input Area */}
                {renderInputArea()}

                {/* Tabs Interface - moved below input */}
                {renderTabs()}
            </>
        );
    }


    // Get workspace info from the mapping
    const getWorkspaceInfo = (workspaceType: string) => {
        return WORKSPACE_BUTTON_MAPPING[workspaceType] || { title: 'Workspace', icon: 'website' }
    }

    // Render the workspace section (used for desktop side-by-side)
    const renderWorkspace = () => {
        if (!isWorkspaceVisible || !workspaceContent) {
            return null;
        }

        const { type, data, title } = workspaceContent;

        // Show building state if actively building
        if (isBuilding && buildingMessage) {
            return (
                <AssistantWorkspaceWrapper
                    title="Building Website"
                    icon="website"
                    isLoading={true}
                    onClose={hideWorkspace}
                />
            );
        }

        const workspaceInfo = getWorkspaceInfo(type)

        return (
            <AssistantWorkspaceWrapper
                title={title || workspaceInfo.title}
                icon={workspaceInfo.icon}
                onClose={hideWorkspace}
            >
                {renderWorkspaceContent()}
            </AssistantWorkspaceWrapper>
        );
    };

    // Render workspace content (shared between desktop and mobile)
    const renderWorkspaceContent = () => {
        if (!workspaceContent) return null;

        const { type, data } = workspaceContent;

        switch (type) {
            case 'website':
                return (
                    <AssistantWorkspaceWebsitePreview
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'templateSelection':
                return (
                    <AssistantWorkspaceTemplateSelection
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'campaignPreview':
                return (
                    <AssistantWorkspaceCampaignApproval
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'facebookConnect':
                return (
                    <AssistantWorkspaceFacebookConnect
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                        onClose={hideWorkspace}
                    />
                )
            case 'marketingPlan':
                return (
                    <AssistantWorkspaceMarketingPlan
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'instagramPostSelection':
                return (
                    <AssistantWorkspaceInstagramPostSelection
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'contentAnalysis':
                return (
                    <AssistantWorkspaceContentAnalysis
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )

            case 'report':
                return (
                    <div className="assistantWorkspaceReport" style={sty.assistantWorkspaceReport}>
                        <pre style={sty.assistantWorkspaceReportContent}>
                            {JSON.stringify(data, null, 2)}
                        </pre>
                    </div>
                )
            case 'preview':
                return (
                    <div className="assistantWorkspacePreview" style={sty.assistantWorkspacePreview}>
                        {data.imageUrl && (
                            <img
                                src={data.imageUrl}
                                alt="Preview"
                                style={sty.assistantWorkspacePreviewImage}
                            />
                        )}
                        {data.content && (
                            <div style={sty.assistantWorkspacePreviewContent}>
                                {data.content}
                            </div>
                        )}
                    </div>
                )
            case 'component':
                return (
                    <div className="assistantWorkspaceComponent" style={sty.assistantWorkspaceComponent}>
                        {data.component || <div>Custom component would render here</div>}
                    </div>
                )
            case 'custom':
                return (
                    <div className="assistantWorkspaceCustom" style={sty.assistantWorkspaceCustom}>
                        {data}
                    </div>
                )
            default:
                return <div>Unknown workspace type: {type}</div>
        }
    };

    // Render the workspace as a modal overlay (mobile only)
    const renderWorkspaceModal = () => {
        if (!isWorkspaceVisible || !workspaceContent) {
            return null;
        }

        const { type, title } = workspaceContent;


        return (
            <div
                className="assistantWorkspaceModal"
                style={styx(sty.assistantWorkspaceModal, mobileView && sty.assistantWorkspaceModal_mobile)}
            >
                {/* Backdrop */}
                <div
                    className="assistantWorkspaceModalBackdrop"
                    style={sty.assistantWorkspaceModalBackdrop}
                    onClick={hideWorkspace}
                />

                {/* Modal Content */}
                <div
                    className="assistantWorkspaceModalContent"
                    style={styx(
                        sty.assistantWorkspaceModalContent,
                        mobileView && sty.assistantWorkspaceModalContent_mobile
                    )}
                >
                    <AssistantWorkspaceWrapper
                        title={isBuilding ? "Building Website" : (title || getWorkspaceInfo(type).title)}
                        icon={isBuilding ? "website" : getWorkspaceInfo(type).icon}
                        isLoading={isBuilding}
                        onClose={hideWorkspace}
                        className="modalWorkspace"
                    >
                        {!isBuilding && renderWorkspaceContent()}
                    </AssistantWorkspaceWrapper>
                </div>
            </div>
        );
    };

    // Render the music selector modal
    const renderMusicSelectorModal = () => {
        return (
            <ModalSymphony
                className="musicSelectorModal"
                isOpen={showMusicSelector}
                onClose={() => setShowMusicSelector(false)}
                title="Select a Release to Promote"
                size="medium"
                isIsolateModalContent
                style={{
                    maxHeight: '500px',
                }}
            >
                <div className="musicSelectorModalContentInner" style={sty.musicSelectorModalContentInner}>
                    <RecordSearchView
                        handleUnifiedRecordSelect={handleMusicSelectorRecordSelect}
                        letUserSearchSpotify={true}
                        options={['single', 'project', 'playlist']}
                    />
                </div>
            </ModalSymphony>
        );
    };

    // Render the test workspace modal
    const renderTestWorkspaceModal = () => {
        return (
            <ModalSymphony
                className="testWorkspaceModal"
                isOpen={showTestWorkspaceModal}
                onClose={() => setShowTestWorkspaceModal(false)}
                title="View Test Workspace"
                size="small"
                buttonLeftText="Cancel"
                buttonRightText="Open Workspace"
                onClickButtonLeft={() => setShowTestWorkspaceModal(false)}
                onClickButtonRight={handleTestWorkspaceSubmit}
                buttonLeftVariant="text"
                buttonRightVariant="primary"
            >
                <div className="testWorkspaceModalContent" style={sty.testWorkspaceModalContent}>
                    <div className="testWorkspaceModalDescription" style={sty.testWorkspaceModalDescription}>
                        Enter a Campaign ID to open the Campaign Approval workspace for testing:
                    </div>

                    <input
                        type="text"
                        className="testWorkspaceInput"
                        style={sty.testWorkspaceInput}
                        value={testCampaignId}
                        onChange={(e) => setTestCampaignId(e.target.value)}
                        placeholder="Enter Campaign ID..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleTestWorkspaceSubmit()
                            }
                        }}
                        autoFocus
                    />

                </div>
            </ModalSymphony>
        );
    };

    // Render the load chat modal
    const renderLoadChatModal = () => {
        return (
            <ModalSymphony
                className="loadChatModal"
                isOpen={showLoadChatModal}
                onClose={() => {
                    setShowLoadChatModal(false)
                    setTestChatId('') // Clear input when closing
                }}
                title="Load Chat Conversation"
                size="small"
                buttonLeftText="Cancel"
                buttonRightText="Load Chat"
                onClickButtonLeft={() => {
                    setShowLoadChatModal(false)
                    setTestChatId('') // Clear input when canceling
                }}
                onClickButtonRight={handleLoadChatSubmit}
                buttonLeftVariant="text"
                buttonRightVariant="primary"
            >
                <div className="loadChatModalContent" style={sty.testWorkspaceModalContent}>
                    <div className="loadChatModalDescription" style={sty.testWorkspaceModalDescription}>
                        Enter a Chat ID to load and resume an existing chat conversation:
                    </div>

                    <input
                        type="text"
                        className="loadChatInput"
                        style={sty.testWorkspaceInput}
                        value={testChatId}
                        onChange={(e) => setTestChatId(e.target.value)}
                        placeholder="Enter Chat ID..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleLoadChatSubmit()
                            }
                        }}
                        autoFocus
                    />

                </div>
            </ModalSymphony>
        );
    };

    // Render the demo workspaces modal
    const renderDemoWorkspaceModal = () => {
        const demoWorkspaceOptions = [
            { type: 'templateSelection', title: 'Template Selection', icon: 'file', description: 'Browse website templates' },
            { type: 'website', title: 'Website Preview', icon: 'website', description: 'Preview a demo website' },
            { type: 'campaignPreview', title: 'Campaign Approval', icon: 'marketing', description: 'Review campaign details' },
            { type: 'marketingPlan', title: 'Marketing Plan', icon: 'analytics', description: 'View marketing strategy' },
            { type: 'facebookConnect', title: 'Facebook Connect', icon: 'facebookFlat', description: 'Connect social accounts' },
            { type: 'instagramPostSelection', title: 'Instagram Posts', icon: 'instagram', description: 'Select Instagram content' },
            { type: 'contentAnalysis', title: 'Content Analysis', icon: 'chart', description: 'Analyze content performance' },
        ]

        return (
            <ModalSymphony
                className="demoWorkspaceModal"
                isOpen={showDemoWorkspaceModal}
                onClose={() => setShowDemoWorkspaceModal(false)}
                title="Demo Workspaces"
                size="medium"
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowDemoWorkspaceModal(false)}
                buttonLeftVariant="text"
            >
                <div className="demoWorkspaceModalContent" style={sty.demoWorkspaceModalContent}>
                    <div className="demoWorkspaceModalDescription" style={sty.demoWorkspaceModalDescription}>
                        Choose a workspace to demo with pre-filled data:
                    </div>

                    <div className="demoWorkspaceGrid" style={sty.demoWorkspaceGrid}>
                        {demoWorkspaceOptions.map((option) => (
                            <div
                                key={option.type}
                                className="demoWorkspaceOption"
                                style={sty.demoWorkspaceOption}
                                onClick={() => handleDemoWorkspaceOpen(option.type)}
                            >
                                <div className="demoWorkspaceOptionIcon" style={sty.demoWorkspaceOptionIcon}>
                                    <Icon icon={option.icon} size={24} className="demoOptionIcon" />
                                </div>
                                <div className="demoWorkspaceOptionContent" style={sty.demoWorkspaceOptionContent}>
                                    <div className="demoWorkspaceOptionTitle" style={sty.demoWorkspaceOptionTitle}>
                                        {option.title}
                                    </div>
                                    <div className="demoWorkspaceOptionDescription" style={sty.demoWorkspaceOptionDescription}>
                                        {option.description}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </ModalSymphony>
        );
    };

    // Render the job details modal
    const renderJobDetailsModal = () => {
        if (!selectedJobDetails) return null;

        const isAssetJob = 'assetType' in selectedJobDetails;
        const isCampaignJob = 'playbookType' in selectedJobDetails;

        return (
            <ModalSymphony
                className="jobDetailsModal"
                isOpen={showJobDetailsModal}
                onClose={() => setShowJobDetailsModal(false)}
                title={isAssetJob ? 'Asset Generation Details' : 'Campaign Details'}
                size="large"
            >
                <div className="jobDetailsModalContent" style={sty.jobDetailsModalContent}>
                    {isAssetJob && (
                        <div className="assetJobDetails" style={sty.assetJobDetails}>
                            <div className="jobDetailHeader" style={sty.jobDetailHeader}>
                                <h3 style={sty.jobDetailTitle}>
                                    {(selectedJobDetails as AssetJobDetails).assetType === 'video' ? '🎬' : '🎨'}
                                    {' '}{(selectedJobDetails as AssetJobDetails).assetType.charAt(0).toUpperCase() + (selectedJobDetails as AssetJobDetails).assetType.slice(1)} Generation
                                </h3>
                                <div
                                    className="jobDetailStatus"
                                    style={styx(
                                        sty.jobDetailStatus,
                                        { backgroundColor: (selectedJobDetails as AssetJobDetails).status === 'completed' ? Colors.green + '20' : Colors.purple + '20' }
                                    )}
                                >
                                    {(selectedJobDetails as AssetJobDetails).status}
                                </div>
                            </div>

                            <div className="jobDetailContent" style={sty.jobDetailContent}>
                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Prompt:</strong>
                                    <p>{(selectedJobDetails as AssetJobDetails).generationPrompt}</p>
                                </div>

                                {(selectedJobDetails as AssetJobDetails).resultMetadata && (
                                    <div className="jobDetailField" style={sty.jobDetailField}>
                                        <strong>Result:</strong>
                                        {(selectedJobDetails as AssetJobDetails).assetType === 'video' ? (
                                            <video
                                                controls
                                                style={sty.assetPreview}
                                                src={(selectedJobDetails as AssetJobDetails).resultMetadata.videoUrl}
                                            />
                                        ) : (
                                            <img
                                                style={sty.assetPreview}
                                                src={(selectedJobDetails as AssetJobDetails).resultMetadata.images?.[0]?.cloudinaryUrl}
                                                alt="Generated asset"
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Created:</strong>
                                    <p>{new Date((selectedJobDetails as AssetJobDetails).createdAt).toLocaleString()}</p>
                                </div>

                                {(selectedJobDetails as AssetJobDetails).generationCompletedAt && (
                                    <div className="jobDetailField" style={sty.jobDetailField}>
                                        <strong>Completed:</strong>
                                        <p>{new Date((selectedJobDetails as any).generationCompletedAt).toLocaleString()}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isCampaignJob && (
                        <div className="campaignJobDetails" style={sty.campaignJobDetails}>
                            <div className="jobDetailHeader" style={sty.jobDetailHeader}>
                                <h3 style={sty.jobDetailTitle}>
                                    🚀 {(selectedJobDetails as CampaignExecutorDetails).playbookType} Campaign
                                </h3>
                                <div
                                    className="jobDetailStatus"
                                    style={styx(
                                        sty.jobDetailStatus,
                                        { backgroundColor: (selectedJobDetails as CampaignExecutorDetails).status === 'COMPLETED' ? Colors.green + '20' : Colors.purple + '20' }
                                    )}
                                >
                                    {(selectedJobDetails as CampaignExecutorDetails).status}
                                </div>
                            </div>

                            <div className="jobDetailContent" style={sty.jobDetailContent}>
                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Completion:</strong>
                                    <div className="progressBarContainer" style={sty.progressBarContainer}>
                                        <div
                                            className="progressBar"
                                            style={styx(sty.progressBar, { width: `${(selectedJobDetails as CampaignExecutorDetails).completionPercentage}%` })}
                                        />
                                    </div>
                                    <span>{(selectedJobDetails as CampaignExecutorDetails).completionPercentage}%</span>
                                </div>

                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Field Status:</strong>
                                    <div className="fieldStatusGrid" style={sty.fieldStatusGrid}>
                                        {Object.entries((selectedJobDetails as CampaignExecutorDetails).fieldStatus).map(([field, status]) => (
                                            <div key={field} className="fieldStatusItem" style={sty.fieldStatusItem}>
                                                <span style={status ? sty.fieldStatusComplete : sty.fieldStatusIncomplete}>
                                                    {status ? '✓' : '○'}
                                                </span>
                                                <span>{field.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Created:</strong>
                                    <p>{new Date((selectedJobDetails as CampaignExecutorDetails).createdAt).toLocaleString()}</p>
                                </div>

                                <div className="jobDetailField" style={sty.jobDetailField}>
                                    <strong>Ready for Submission:</strong>
                                    <p>{(selectedJobDetails as CampaignExecutorDetails).readyForSubmission ? 'Yes' : 'No'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ModalSymphony>
        );
    };

    const renderAddMessageModal = () => {
        return (
            <ModalSymphony
                className="addMessageModal"
                isOpen={showAddMessageModal}
                onClose={() => {
                    setShowAddMessageModal(false)
                    setNewMessageText('')
                    setNewMessageIsUser(true)
                }}
                title="Add Custom Message"
            >
                <div className="addMessageModalContent" style={sty.addMessageModalContent}>
                    <div className="messageTypeSelector" style={sty.messageTypeSelector}>
                        <label style={sty.radioLabel}>
                            <input
                                type="radio"
                                checked={newMessageIsUser}
                                onChange={() => setNewMessageIsUser(true)}
                                style={sty.radioInput}
                            />
                            User Message
                        </label>
                        <label style={sty.radioLabel}>
                            <input
                                type="radio"
                                checked={!newMessageIsUser}
                                onChange={() => setNewMessageIsUser(false)}
                                style={sty.radioInput}
                            />
                            Assistant Message
                        </label>
                    </div>

                    <textarea
                        value={newMessageText}
                        onChange={(e) => setNewMessageText(e.target.value)}
                        placeholder={`Enter ${newMessageIsUser ? 'user' : 'assistant'} message...`}
                        style={sty.addMessageTextarea}
                        autoFocus
                    />

                    <div className="addMessageButtons" style={sty.addMessageButtons}>
                        <ButtonSymphony
                            className="cancelAddMessageButton"
                            variant="text"
                            onClick={() => {
                                setShowAddMessageModal(false)
                                setNewMessageText('')
                                setNewMessageIsUser(true)
                            }}
                        >
                            Cancel
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="addMessageButton"
                            variant="primary"
                            onClick={handleAddMessage}
                            disabled={!newMessageText.trim()}
                        >
                            Add Message
                        </ButtonSymphony>
                    </div>
                </div>
            </ModalSymphony>
        )
    }

    const renderLoadingDots = () => {
        return (
            <div className="loadingDots" style={sty.loadingDots}>
                <div className="loadingDot" style={{ ...sty.loadingDot, animationDelay: '0s' }}></div>
                <div className="loadingDot" style={{ ...sty.loadingDot, animationDelay: '0.2s' }}></div>
                <div className="loadingDot" style={{ ...sty.loadingDot, animationDelay: '0.4s' }}></div>

            </div>
        )
    }

    const renderMaestroIcon = (isLoadingMessage?: boolean) => {

        // let waitingForAIResponse = isLoadingAIResponse && currentStreamedText === '';
        return (
            <Icon
                className={`assistantChatMessageIcon ${isLoadingMessage ? 'waiting' : ''}`}
                icon="maestroBadgeImage"
                size={36}
            // style={
            //     waitingForAIResponse
            //         ? sty.loadingIndicatorPulse
            //         : undefined
            // }
            />
        )

    }

    // Render the chat mode view
    const renderChat = () => {
        // Create streaming message with parts from streamingState
        const streamingMessage = (isLoadingAIResponse || isStreaming) ? {
            id: 'thinking-message',
            isUser: false,
            text: currentStreamedText || '', // Keep for backward compatibility
            parts: [...streamingState.currentParts], // ADD THIS: Include current parts
            isStreaming: true,
            isLoadingMessage: isLoadingAIResponse && !isStreaming, // Only show loading when not streaming yet
            createdAt: new Date().toISOString()
        } : null;

        // Combine stored messages with streaming message
        const allMessages = streamingMessage
            ? [...storeMessages, streamingMessage]
            : storeMessages;


        return (
            <div className="assistantChatContainer" style={styx(sty.assistantChatContainer, isChat && sty.assistantChatContainer_chatMode)}>
                <div
                    ref={messagesContainerRef}
                    className="assistantChatMessages"
                    style={styx(
                        mStyle(sty, "assistantChatMessages")
                    )}
                >
                    {allMessages.map((messageItem: MessageItem, index) => (
                        <Fragment key={makeKey(messageItem.id, index)}>
                            {renderMessage(messageItem)}
                        </Fragment>
                    ))}

                    <div
                        className='assistantChatMessagesEnd'
                        ref={messagesEndRef}
                        style={{
                            marginTop: -16,
                        }}
                    />
                </div>

                {/* Quick Actions */}
                {availableQuickActions.length > 0 && (
                    <div className="quickActionsWrapper" style={mStyle(sty, "quickActionsWrapper")}>
                        <QuickActions
                            actions={availableQuickActions}
                            onActionClick={handleQuickActionClick}
                        />
                    </div>
                )}

                {/* Scroll to Bottom Button */}
                {showScrollToBottom && (
                    <div
                        className="scrollToBottomWrapper"
                        style={styx(
                            sty.scrollToBottomWrapper,
                            mobileView && sty.scrollToBottomWrapper_mobile
                        )}
                    >
                        <button
                            className="scrollToBottomButton"
                            style={sty.scrollToBottomButton}
                            onClick={() => scrollToBottom(true)}
                            type="button"
                        >
                            <Icon
                                className="scrollToBottomIcon"
                                icon="arrowDown"
                                size={20}
                                color="white"
                            />
                        </button>
                    </div>
                )}

                {renderInputArea()}
                {isChat && !mobileView && renderDisclaimer()}
            </div>
        );
    }

    // feedback prompt renderer
    const renderFeedbackPrompt = () => {
        if (!showFeedbackPrompt || hasSubmittedConversationFeedback || !isChat) return null
        return (
            <div className="assistantFeedbackPrompt" style={styx(mStyle(sty, "assistantFeedbackPrompt"), showFeedbackPrompt ? sty.feedbackPrompt_visible : sty.feedbackPrompt_hidden)}>
                <span style={sty.feedbackPromptText}>How is Maestro doing so far?</span>
                <div style={sty.feedbackButtons}>
                    <ButtonSymphony
                        className="feedbackGood"
                        variant="primary"
                        onClick={() => handleFeedbackSubmit('good')}
                        iconLeft="like"
                    >
                        Good!
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="feedbackBad"
                        variant="primary"
                        onClick={() => handleFeedbackSubmit('bad')}
                        iconLeft="closeCircle"
                    >
                        Not great
                    </ButtonSymphony>
                </div>
            </div>
        )
    }



    // Render upload file preview
    const renderUploadFilePreview = () => {
        // Show both uploading files and uploaded assets (until message is sent)
        const allFiles = [...uploadingFiles, ...uploadedAssets]
        if (allFiles.length === 0) return null;

        return (
            <div
                className="universalUploadInline"
                style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    marginBottom: 8,
                    zIndex: 100
                }}
            >
                {/* Horizontal file row */}
                <div style={{
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto',
                    maxWidth: '100%',
                    paddingBottom: 4,
                    scrollbarWidth: 'thin',
                    msOverflowStyle: 'auto'
                }}>
                    {allFiles.map((file) => {
                        return (
                            <div key={file.id} style={{ position: 'relative' }}>
                                <UploadedFilePreview
                                    file={file}
                                    layout="horizontal"
                                    onClick={() => handleUploadPreviewClick(file)}
                                    showProgress={true}
                                />
                                {/* Custom error/retry overlay */}
                                {file.status === 'error' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        display: 'flex',
                                        gap: 4,
                                        alignItems: 'center'
                                    }}>
                                        {(!file.retryCount || file.retryCount < 3) && (
                                            <div
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    backgroundColor: Colors.yellow,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => retryUpload(file.id, chatId || undefined)}
                                                title={`Retry upload (${file.retryCount || 0}/3 attempts)`}
                                            >
                                                <Icon className="statusIcon" icon="refresh" size={10} color="white" />
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                backgroundColor: Colors.red,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleRemoveUploadingFile(file.id)}
                                            title="Remove file"
                                        >
                                            <Icon className="statusIcon" icon="close" size={10} color="white" />
                                        </div>
                                    </div>
                                )}
                                {/* Custom close button for non-error states */}
                                {file.status !== 'error' && file.status !== 'ready' && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            backgroundColor: Colors.greyLight,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleRemoveUploadingFile(file.id)}
                                    >
                                        <Icon className="statusIcon" icon="close" size={10} color={Colors.greyDark} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Handle remove uploading file
    const handleRemoveUploadingFile = (fileId: string) => {
        const fileToRemove = uploadingFiles.find(f => f.id === fileId);
        if (fileToRemove?.status === 'error') {
            handleUploadError(fileId, 'Upload failed');
        }
        updateUploadingFile(fileId, { status: 'ready' }); // Mark as done to remove from UI
    };

    return (
        <div className="assistantChatWrapper" style={styx(sty.assistantChatWrapper, isChat && sty.assistantChatWrapper_chat)}>
            {/* Top navigation bar for chat mode */}
            {renderChatTopBar()}

            <div
                className="assistantChatComponent"
                style={styx(
                    mStyle(sty, "assistantChatComponent"),
                    isChat ? mStyle(sty, "assistantChatComponent_chat") : mStyle(sty, "assistantChatComponent_question"),
                    customChatComponentStyling ? { ...customChatComponentStyling } : {}
                )}
            >
                {/* Main chat content */}
                {isChat && isWorkspaceVisible && !mobileView ? (
                    // Desktop: Side-by-side layout
                    <div
                        className="assistantChatWithWorkspace"
                        style={mStyle(sty, "assistantChatWithWorkspace")}
                    >
                        <div className="assistantChatSection" style={sty.assistantChatSection}>
                            {renderChat()}
                        </div>
                        <div className="assistantWorkspaceSection" style={sty.assistantWorkspaceSection}>
                            {renderWorkspace()}
                        </div>
                    </div>
                ) : isChat ? (
                    // Chat without workspace or mobile with workspace (uses modal)
                    renderChat()
                ) : (
                    // Initial question view
                    renderQuestion()
                )}

                {/* Workspace Modal Overlay - Mobile Only */}
                {isWorkspaceVisible && mobileView && renderWorkspaceModal()}

                {/* Music Selector Modal */}
                {renderMusicSelectorModal()}

                {/* Test Workspace Modal */}
                {renderTestWorkspaceModal()}

                {/* Load Chat Modal */}
                {renderLoadChatModal()}

                {/* Demo Workspaces Modal */}
                {renderDemoWorkspaceModal()}
                {/* Add Message Modal */}
                {showAddMessageModal && renderAddMessageModal()}

                <SignupFlowModal className="assistantChatSignupFlowModal" />

                {/* Message Limit Enforcement Modal */}
                <MessageLimitEnforcementModal
                    className="assistantChatMessageLimitEnforcementModal"
                    isOpen={isEnforcementModalOpen}
                    onClose={closeEnforcementModal}
                />

                {/* Render Lightbox Modal */}
                {renderLightboxModal()}

            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    assistantChatWrapper: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        height: 'auto',
        outline: 'none', // Remove default outline from react-dropzone
    },
    dropzoneActive: {
        borderColor: Colors.purple,
        borderWidth: 2,
        borderStyle: 'dashed',
        backgroundColor: 'rgba(136, 0, 255, 0.05)', // Light purple tint
    },
    assistantChatWrapper_chat: {
        height: '100%'
    },
    assistantChatComponent: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: 'transparent',
        paddingTop: '4rem',
        paddingBottom: 0,
        flex: 'unset'
    },
    assistantChatComponent_mobile: {
        overflow: 'auto',
    },
    assistantChatComponent_chat: {
        height: '100%',
        paddingTop: 0,
    },
    assistantChatComponent_question: {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        maxWidth: 800,
        margin: '0 auto',
        width: '100%'
    },
    assistantChatComponent_question_mobile: {
        justifyContent: 'flex-start',
        paddingTop: '6rem',
        paddingLeft: 18,
        paddingRight: 18
    },
    assistantChatContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 800,
        margin: '0 auto',
        width: '100%',
        padding: '0 24px',
        position: 'relative',
    },
    assistantChatContainer_chatMode: {
        padding: 0,
    },

    assistantChatInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        fontSize: 20,
        borderRadius: 8,
        transition: 'all 0.2s ease',
        outline: 'none',
        maxWidth: 600,
        width: '100%',
        marginBottom: 24,
        backgroundColor: 'rgba(136, 0, 255, 0.16)',
        borderColor: 'rgb(136, 0, 255)',
        borderWidth: 2,
        borderStyle: 'solid',
        boxShadow: '0px 0px 0px 2px rgba(136, 0, 255, 0.16)',
        backdropFilter: 'blur(10px)',
    },
    assistantChatInputContainer_mobile: {
        minHeight: 120,
        marginBottom: 16,
        padding: 8,
    },
    assistantChatInputContainer_chatMode: {
        borderRadius: 8,
        width: '100%',
        maxWidth: 'unset',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        backdropFilter: 'unset',
        marginBottom: 8

    },
    assistantChatInputContainer_chatMode_mobile: {
        marginBottom: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    assistantChatInputContainerWrapper: {
        padding: 24,
        borderTop: Sty.border,
        marginTop: 0,
        marginBottom: 24,
        position: 'relative',
        zIndex: 999, // Above workspace modal
        backgroundColor: 'white',
        maxWidth: 800,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },

    assistantChatInputContainerWrapper_mobile: {
        marginBottom: 16,
    },

    assistantChatInputContainerWrapper_chatMode_mobile: {
        padding: 0,
        position: 'sticky',
        bottom: 0,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 0,
    },
    assistantChatInput: {
        flex: 1,
        fontSize: 18,
        resize: 'none',
        minHeight: 75,
        maxHeight: 120,
        outline: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        lineHeight: 1.4,
        marginBottom: 8,
        color: 'white'
    },
    assistantChatInput_mobile: {
        fontSize: 16,
    },
    assistantChatCharacterInfo: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        minHeight: 16,
        marginBottom: 8,
        fontSize: 12,
    },
    characterCount: {
        color: Colors.greyDark,
        fontSize: 12,
    },
    characterCountWarning: {
        color: '#f59e0b', // Orange warning color
        fontSize: 12,
        fontWeight: 500,
    },
    characterLimitWarning: {
        color: Colors.red,
        fontSize: 12,
        fontWeight: 600,
        animation: 'fadeInUp 0.2s ease-out',
    },
    assistantChatQuestion: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 24
    },
    assistantChatQuestion_mobile: {
        marginBottom: 16,
    },
    questionDevModeToggle: {
        position: 'relative',
        zIndex: 10,
    },
    assistantChatMessages: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        paddingBottom: 64,
        overflowY: 'auto',
        gap: 24,
        flex: 1,
        minHeight: 0,
        scrollBehavior: 'smooth', // Enable smooth scrolling
    },
    assistantChatMessages_mobile: {
        paddingLeft: 8,
        paddingRight: 8,
    },
    message: {
        display: 'flex',
        maxWidth: '80%',
        padding: 12,
        borderRadius: 16,
        position: 'relative',
        fontSize: 18,
    },
    message_mobile: {
        fontSize: 16,
    },
    userMessage: {
        alignSelf: 'flex-end',
        backgroundColor: '#3e356f',
        color: 'white',
        margin: '0 24px',
        marginRight: 0,
    },

    assistantMessage: {
        alignSelf: 'flex-start',
        backgroundColor: 'transparent',
        color: Colors.textColor,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        maxWidth: '100%',
        padding: '0 24px',
        borderRadius: 0,
        paddingLeft: 0,
    },

    messageContent: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.6,
        flex: 1,
        paddingRight: 8,
        position: 'relative',
        marginTop: 5

    },

    thinkingMessage: {
        fontStyle: 'italic',
        color: Colors.greyDark,
    },
    assistantChatActionButton: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        backgroundColor: 'transparent',
        border: `1px solid ${Colors.purpleLight}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: 8,
        color: Colors.purpleLighter,
        height: 36,
        width: 36,
    },
    assistantChatActionButton_mobile: {
        padding: 6,
        height: 32,
        width: 32,
    },
    assistantChatTabsContainer: {
        width: 750,
        margin: '0 auto',
    },
    assistantChatTabsContainer_mobile: {
        width: '100%',
    },
    assistantChatSendButton: {
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: 8,
        borderRadius: 6,
        backgroundColor: Colors.purple,
        color: 'white',
    },
    mediaContainer: {
        marginBottom: 8,
        borderRadius: 8,
        overflow: 'hidden',
        maxWidth: '100%',
    },
    mediaThumbnail: {
        maxWidth: '100%',
        maxHeight: 200,
        objectFit: 'cover',
        cursor: 'pointer',
        display: 'block'
    },
    assistantChatTitle: {
        textAlign: 'center',
        fontSize: 48,
        lineHeight: 2,
        color: Colors.white,
        fontWeight: 400
    },
    assistantChatTitle_mobile: {
        fontSize: 28,
        lineHeight: 1.2
    },
    videoContainer: {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
    },
    disclaimer: {
        fontSize: '0.9rem',
        color: Colors.greyLight,
        marginTop: 4,
        marginBottom: 8,
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    playOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '50%',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    playIcon: {
        color: 'white',
    },
    assistantChatInputActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 'auto',
    },
    assistantChatLeftActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    maestroChangeLogButton: {
        backgroundColor: 'transparent',
        border: `1px solid ${Colors.purpleLight}`,
        borderRadius: 6,
        height: 36,
        width: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    maestroChangeLogButton_mobile: {
        height: 32,
        minWidth: 45,
    },
    // playbookTagsContainer: { // Commented out as its usage is commented out
    //     display: 'flex',
    //     flexWrap: 'wrap',
    //     gap: 8,
    // },
    newChatButton: {
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: 'none',
        color: Colors.greyDark,
        fontSize: 14,
        fontFamily: 'inherit',
        padding: '4px 8px',
        transition: 'all 0.2s ease',
        borderRadius: 4,
    },
    assistantChatSubtitle: {
        fontSize: 20,
        lineHeight: 1.8,
        color: Colors.white,
        fontWeight: 400,
    },
    assistantChatSubtitle_mobile: {
        fontSize: 14,
        marginTop: 12
    },
    newChatButtonTopRight: {
        position: 'absolute',
        top: 8,
        right: 8,
        fontSize: 12,
        color: Colors.greyDark,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '4px 8px',
        borderRadius: 12,
        border: `1px solid ${Colors.grey}`,
    },
    /** Styles for loading indicators */
    loadingTextWithIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        backgroundColor: Colors.greyLight,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 14,
        fontStyle: 'italic',
        color: Colors.greyDark,
        marginBottom: 4,
    },
    loadingIndicator: {
        animation: 'spin 1s linear infinite',
    },
    loadingIndicatorPulse: {
        animation: 'pulse 1.5s ease-in-out infinite',
    },
    loadingDots: {
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        position: 'absolute',
        left: 47,
        top: '50%',
        transform: 'translateY(-50%)',
    },
    loadingDot: {
        animation: 'bounce 1.4s ease-in-out infinite both',
        fontSize: 20,
        lineHeight: 1,
        width: 10,
        height: 10,
        borderRadius: '50%', // It's a good practice to use string values for percentage        
        backgroundColor: Colors.purpleLight,
    },
    /** Workspace styles */
    assistantChatWithWorkspace: {
        display: 'flex',
        height: '100%',
        gap: 0,
        width: '100%',
        margin: 'auto',
        justifyContent: 'center',
    },
    assistantChatSection: {
        flex: '0 0 600px', // Fixed width for chat section
        minWidth: 400,
        maxWidth: 650,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        paddingLeft: 24,
        paddingRight: 24,
    },
    assistantWorkspaceSection: {
        flex: 1, // Take up remaining space
        minWidth: 0, // Allow shrinking
        borderLeft: Sty.border,
        paddingLeft: 24,
    },
    assistantWorkspaceReport: {
        width: '100%',
    },
    assistantWorkspaceReportContent: {
        backgroundColor: Colors.greyLight,
        padding: 16,
        borderRadius: 8,
        fontSize: 12,
        fontFamily: 'monospace',
        overflow: 'auto',
        maxHeight: 400,
        margin: 0,
    },
    assistantWorkspacePreview: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    assistantWorkspacePreviewImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
    },
    assistantWorkspacePreviewContent: {
        padding: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        fontSize: 14,
        lineHeight: 1.5,
    },
    assistantWorkspaceComponent: {
        width: '100%',
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `2px dashed ${Colors.grey}`,
        color: Colors.greyDark,
        fontSize: 14,
    },
    assistantWorkspaceCustom: {
        width: '100%',
    },
    /** Top bar styles for chat mode */
    assistantChatTopBar: {
        position: 'sticky',
        top: 0,
        width: '100%',
        height: 60,
        padding: '0 24px',
        borderBottom: `1px solid ${Colors.grey}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 800,
        flexShrink: 0,
        backgroundColor: 'rgb(44, 33, 53)',
        border: 'none',
    },
    assistantChatTopBar_mobile: {
        height: 45,
        padding: '0 16px',
    },
    assistantChatTopBarLeft: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },
    assistantChatTopBarCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    assistantChatTopBarRight: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flex: 1,
    },
    assistantChatBackButton: {
        display: 'flex',
        padding: 0,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    assistantChatNewChatButton: {
        borderRadius: 99,
        fontSize: 14,
        padding: '8px 12px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${Colors.purple}`,
    },
    assistantChatGetStartedButton: {
        fontSize: 16,
        marginRight: 12,
    },
    assistantChatLoginButton: {
        fontSize: 16,
    },
    workspaceButton: {
        marginTop: 8,
        marginRight: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    workspaceButtonsContainer: {
        marginTop: 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    assistantWorkspaceModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000, // Above messages but below chat input
        padding: 16,
    },
    assistantWorkspaceModal_mobile: {
        padding: 0,
        alignItems: 'flex-end', // Slide up from bottom on mobile
    },
    assistantWorkspaceModalBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'pointer',
    },
    assistantWorkspaceModalContent: {
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '90vw',
        maxHeight: '85vh',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        zIndex: 1001,
        marginBottom: 120, // Space for chat input
    },
    assistantWorkspaceModalContent_mobile: {
        maxWidth: '100vw',
        maxHeight: '100%', // Account for top bar + chat input
        borderRadius: '12px 12px 0 0',
        marginBottom: 0
    },
    assistantWorkspaceModalContentInner: {
        flex: 1,
        overflowY: 'auto',
        padding: 24,
        paddingTop: 16,
    },
    assistantWorkspaceModalContentInner_mobile: {
        padding: 16,
        paddingTop: 12,
    },
    /** System message styles */
    systemMessagesInlineWithIcon: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 8,
    },
    systemMessageInline: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        padding: '4px 8px',
        fontSize: 12,
        color: Colors.greyDark,
        border: `1px solid ${Colors.grey}`,
        alignSelf: 'flex-start',
        maxWidth: '100%',
        fontStyle: 'italic',
    },
    systemMessage_router_start: {
        backgroundColor: '#f0f9ff',
        borderColor: '#0ea5e9',
        color: '#0369a1',
    },
    systemMessage_specialist_activated: {
        backgroundColor: '#f0fdf4',
        borderColor: '#22c55e',
        color: '#15803d',
    },
    systemMessage_tool_call_start: {
        backgroundColor: '#fefce8',
        borderColor: '#eab308',
        color: '#a16207',
    },
    systemMessage_tool_call_end: {
        backgroundColor: '#f0fdf4',
        borderColor: '#22c55e',
        color: '#15803d',
        animation: 'none',
    },

    systemMessageText: {
        fontWeight: 500,
    },
    /** Tool execution styles */
    toolExecutionContainer: {
        padding: '0 24px',
        marginBottom: 12,
        display: 'flex',
        justifyContent: 'flex-start',
    },
    toolExecutionTag: {
        animation: 'fadeInUp 0.3s ease-out',
    },
    streamingTextContainer: {
        // No animation - let text appear naturally as it streams in
        fontStyle: 'normal',
        color: Colors.white,
    },
    toolExecutionMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#f0f9ff',
        borderRadius: 12,
        padding: '12px 16px',
        fontSize: 15,
        color: '#0369a1',
        border: '1px solid #0ea5e9',
        fontStyle: 'italic',
        animation: 'fadeInUp 0.3s ease-out',
    },
    toolExecutionIcon: {
        flexShrink: 0,
        animation: 'spin 1s linear infinite',
    },
    toolExecutionText: {
        fontWeight: 500,
        flex: 1,
    },
    toolExecutionDots: {
        display: 'flex',
        gap: 2,
        alignItems: 'center',
    },
    toolExecutionDot: {
        animation: 'bounce 1.4s ease-in-out infinite both',
        fontSize: 20,
        lineHeight: 1,
        color: '#0369a1',
    },
    workspaceReopenButtons: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        marginBottom: 16,
    },
    workspaceReopenTitle: {
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 12,
    },
    workspaceReopenButtonsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    workspaceReopenButton: {
        backgroundColor: Colors.purple,
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    persistentSystemMessages: {
        padding: '0 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginTop: 16,
        alignItems: 'flex-start'
    },
    summaryCard: {
        display: 'flex',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        maxWidth: 400,
    },
    summaryCard_wide: {
        flexDirection: 'column',
        maxWidth: 300,
    },
    summaryCardLink: {
        textDecoration: 'none',
        color: 'inherit',
    },
    summaryCardImage: {
        width: 80,
        height: 80,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_wide: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_tall: {
        width: 60,
        height: 100,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardContent: {
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
    },
    summaryCardTitle: {
        fontWeight: 'bold',
        fontSize: 16,
        marginBottom: 4,
        color: Colors.textColor,
    },
    summaryCardSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },


    quickActionsWrapper: {
        padding: 16,
        marginBottom: 16,
        marginLeft: 24,
        marginRight: 24,
    },
    quickActionsWrapper_mobile: {
        marginLeft: 4,
        marginRight: 4,
        marginBottom: 4,
    },
    // Base card styles for all video generation states
    videoGenerationInlineCard: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        borderRadius: 12,
        border: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        animation: 'fadeInUp 0.4s ease-out',
    },

    // In-progress state styles
    videoGenerationInlineInProgress: {
        padding: 16,
        gap: 12,
    },
    videoGenerationInlineInProgress_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    },

    // Completed state styles
    videoGenerationInlineCompleted: {
        padding: 0,
        cursor: 'pointer',
        maxWidth: 300,
    },
    videoGenerationInlineCompleted_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },

    // Aspect ratio specific styles for completed state
    videoGenerationInlineCompleted_wide: {
        maxWidth: 400,
    },
    videoGenerationInlineCompleted_square: {
        maxWidth: 320,
    },
    videoGenerationInlineCompleted_tall: {
        maxWidth: 280,
    },

    // Video container styles
    videoGenerationInlineVideoContainer: {
        position: 'relative',
        overflow: 'hidden',
    },
    videoGenerationInlineVideoContainer_wide: {
        aspectRatio: '16/9',
    },
    videoGenerationInlineVideoContainer_square: {
        aspectRatio: '1/1',
    },
    videoGenerationInlineVideoContainer_tall: {
        aspectRatio: '9/16',
    },

    videoGenerationInlineVideoThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'all 0.3s ease',
    },

    videoGenerationInlinePlayOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '50%',
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
    },

    videoGenerationInlinePlayIcon: {
        color: 'white',
        marginLeft: 3, // Slight adjustment for visual centering
    },

    videoGenerationInlineSuccessBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: Colors.green,
        color: 'white',
        padding: '6px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },

    videoGenerationInlineSuccessIcon: {
        color: 'white',
    },

    videoGenerationInlineSuccessText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 600,
    },

    videoGenerationInlineVideoInfo: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    videoGenerationInlineVideoTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
    },

    videoGenerationInlineVideoSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    videoGenerationInlineVideoStats: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: Colors.greyDark,
    },

    videoGenerationInlineStatsText: {
        fontSize: 12,
        color: Colors.greyDark,
    },

    // Failed state styles
    videoGenerationInlineFailed: {
        padding: 20,
        gap: 16,
        alignItems: 'center',
        textAlign: 'center',
        borderColor: Colors.red,
        backgroundColor: '#fef2f2',
    },
    videoGenerationInlineFailed_hover: {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    },

    videoGenerationInlineFailedIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    videoGenerationInlineFailedIconSvg: {
        color: Colors.red,
    },

    videoGenerationInlineFailedInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    videoGenerationInlineFailedTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.red,
    },

    videoGenerationInlineFailedSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    // Legacy styles (keeping for backward compatibility)
    videoGenerationInline: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 8,
    },
    videoGenerationInline_hover: {
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        padding: '4px 8px',
    },
    videoGenerationInlineHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        fontWeight: 500,
        color: Colors.textColor,
    },
    videoGenerationInlineTitleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    videoGenerationInlineIcon: {
        marginRight: 6,
    },
    videoGenerationInlineTitles: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    videoGenerationInlineTitle: {
        fontWeight: 'bold',
        fontSize: 16,
    },
    videoGenerationInlineSubtitle: {
        fontSize: 14,
    },
    videoGenerationInlineProgress: {
        marginTop: 4,
        marginBottom: 4,
        height: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 6,
        overflow: 'hidden',
    },
    videoGenerationInlineProgressBar: {
        height: '100%',
        backgroundColor: Colors.purple,
    },
    videoGenerationInlineProgressFill: {
        height: '100%',
        backgroundColor: Colors.purple,
    },
    videoGenerationInlineProgressText: {
        fontWeight: 500,
        fontSize: 14,
        textAlign: 'right',
        paddingRight: 4,
    },
    videoGenerationInlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    videoGenerationInlineStatusIcon: {
        marginRight: 4,
    },
    videoGenerationInlineStatusText: {
        fontWeight: 500,
        fontSize: 14,
    },
    videoGenerationInlineEstimate: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    videoGenerationInlineEstimateIcon: {
        marginRight: 4,
    },
    videoGenerationInlineEstimateText: {
        fontWeight: 500,
        fontSize: 14,
    },
    /** Music selector modal styles */
    musicSelectorModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200, // Above workspace modal
        padding: 16,
    },
    musicSelectorModal_mobile: {
        padding: 0,
        alignItems: 'flex-end',
    },
    musicSelectorModalBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'pointer',
    },
    musicSelectorModalContent: {
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '90vw',
        maxHeight: '85vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        zIndex: 1201,
    },
    musicSelectorModalContent_mobile: {
        maxWidth: '100vw',
        maxHeight: '90vh',
        borderRadius: '12px 12px 0 0',
    },
    musicSelectorModalHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderBottom: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
    },
    musicSelectorModalTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: Colors.textColor,
    },
    musicSelectorModalCloseButton: {
        padding: '8px 12px',
        color: Colors.greyDark,
    },
    musicSelectorModalContentInner: {
        flex: 1,
        overflowY: 'auto',
        padding: 0,
    },
    /** Dev Mode Tools styles */
    devModeToolsContainer: {
        marginBottom: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
    },
    devModeToolButton: {
        backgroundColor: Colors.greyLight,
        color: Colors.textColor,
        border: `1px solid ${Colors.grey}`,
        padding: '8px 16px',
        borderRadius: 6,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flex: 1,
        minWidth: 140,
    },
    testWorkspaceModalContent: {
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    testWorkspaceModalDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.5,
    },
    testWorkspaceInput: {
        width: '100%',
        padding: '12px 16px',
        fontSize: 16,
        border: `1px solid ${Colors.grey}`,
        borderRadius: 6,
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    /** Demo Workspaces Modal styles */
    demoWorkspaceModalContent: {
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    demoWorkspaceModalDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.5,
        textAlign: 'center',
    },
    demoWorkspaceGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        maxHeight: 400,
        overflowY: 'auto',
    },
    demoWorkspaceOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        gap: 12,
    },
    demoWorkspaceOptionIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: 8,
        backgroundColor: Colors.purple + '10',
        color: Colors.purple,
    },
    demoWorkspaceOptionContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    demoWorkspaceOptionTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: Colors.textColor,
    },
    demoWorkspaceOptionDescription: {
        fontSize: 12,
        color: Colors.greyDark,
        lineHeight: 1.3,
    },
    /** Dev Mode styles */
    devModeToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    devModeLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.textColor,
    },
    devModeToggleButton: {
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: 12,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        outline: 'none',
    },
    devModeToggleButton_active: {
        backgroundColor: Colors.purple,
    },
    devModeToggleButton_inactive: {
        backgroundColor: Colors.grey,
    },
    devModeToggleSlider: {
        position: 'absolute',
        top: 2,
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    devModeToggleSlider_active: {
        left: 22,
    },
    devModeToggleSlider_inactive: {
        left: 2,
    },
    messageDevActions: {
        display: 'flex',
        gap: 6,
        marginTop: 8,
        justifyContent: 'flex-end',
        opacity: 1, // Always visible in dev mode
        transition: 'opacity 0.2s ease',
    },
    messageDevActionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        minWidth: 20,
        minHeight: 20,
        padding: 0,
    },
    messageDevActionButton_delete: {
        backgroundColor: Colors.red,
        color: 'white',
    },
    messageDevActionButton_regenerate: {
        backgroundColor: Colors.blue,
        color: 'white',
    },
    messageDevActionButton_resend: {
        backgroundColor: Colors.purple,
        color: 'white',
    },
    /** Job and Chat Item Styles */
    jobItem: {
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    jobItemContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    jobItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    jobItemIcon: {
        fontSize: 24,
        flexShrink: 0,
    },
    jobItemInfo: {
        flex: 1,
        minWidth: 0,
    },
    jobItemTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
        margin: 0,
        marginBottom: 4,
    },
    jobItemDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    jobItemRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
    },
    jobItemStatus: {
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
    },
    jobItemProgressContainer: {
        width: 60,
        height: 4,
        backgroundColor: Colors.greyLight,
        borderRadius: 2,
        overflow: 'hidden',
    },
    jobItemProgress: {
        height: '100%',
        borderRadius: 2,
        transition: 'width 0.3s ease',
    },
    jobItemFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 8,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
    jobItemDate: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    jobItemChatLink: {
        fontSize: 12,
        color: Colors.purple,
        fontWeight: 500,
    },
    chatItem: {
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        padding: '10px 16px',
        marginBottom: 8,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    chatItemContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    chatItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    chatItemIcon: {
        fontSize: 24,
        flexShrink: 0,
    },
    chatItemInfo: {
        flex: 1,
        minWidth: 0,
    },
    chatItemTitle: {
        fontSize: 16,
        fontWeight: 500,
        color: Colors.textColor,
        margin: 0,
        marginBottom: 4,
    },
    chatItemDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        margin: 0,
    },
    chatItemRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
    },
    chatItemStatus: {
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
    },
    chatItemFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 8,
        borderTop: `1px solid ${Colors.greyLight}`,
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemDate: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemStarted: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemTasks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
    },
    chatItemTask: {
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
    },
    chatItemTask_campaign: {
        backgroundColor: Colors.purple + '20',
        color: Colors.purple,
    },
    chatItemTask_asset: {
        backgroundColor: Colors.blue + '20',
        color: Colors.blue,
    },
    /** Tab Content Styles */
    tabContent: {
        padding: '24px 0',
        minHeight: 300,
    },
    tabContentContainer: {
        height: 400, // Fixed height for the tab content area
        width: '100%',
        position: 'relative',
    },
    tabContentScrollable: {
        height: '100%',
        overflowY: 'auto',
        padding: '16px 0',
    },
    itemsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 48,
        gap: 12,
        color: Colors.greyDark,
    },
    loadingIcon: {
        animation: 'pulse 1.5s ease-in-out infinite',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 48,
        gap: 12,
        textAlign: 'center',
    },
    emptyIcon: {
        color: Colors.greyDark,
        opacity: 0.5,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: Colors.white,
        margin: 0,
    },
    emptyDescription: {
        fontSize: 14,
        color: Colors.greyLighter,
        margin: 0,
        maxWidth: 300,
    },
    /** Scroll to bottom button styles */
    scrollToBottomWrapper: {
        position: 'absolute',
        bottom: '22%', // Right above input area
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        pointerEvents: 'none', // Allow clicks to pass through wrapper
    },
    scrollToBottomWrapper_mobile: {
        bottom: 150, // Adjust for mobile input area height
    },
    scrollToBottomButton: {
        pointerEvents: 'all', // Re-enable clicks for button
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: Colors.purple,
        border: 'none',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'all 0.2s ease',
        animation: 'fadeInUp 0.3s ease-out',
    },
    /** Add Message Modal Styles */
    addMessageModalContent: {
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    messageTypeSelector: {
        display: 'flex',
        gap: 16,
        marginBottom: 8,
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: Colors.textColor,
        cursor: 'pointer',
    },
    radioInput: {
        margin: 0,
    },
    addMessageTextarea: {
        width: '100%',
        minHeight: 100,
        padding: 12,
        borderRadius: 6,
        border: `1px solid ${Colors.grey}`,
        fontSize: 14,
        fontFamily: 'inherit',
        resize: 'vertical',
        outline: 'none',
        backgroundColor: 'white',
        color: Colors.greyDark,
    },
    addMessageButtons: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end',
    },
    // Styles for uploaded assets display
    uploadedAssetsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 16,
        paddingTop: 16,
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`, // Subtle separator for dark mode
    },
    uploadedAssetItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        borderRadius: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        position: 'relative', // For spinner positioning
        maxWidth: 140, // Slightly larger for better visibility
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    uploadedAssetThumbnail: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: 8,
        cursor: 'pointer', // For lightbox
        transition: 'opacity 0.2s ease',
    },
    uploadedAssetGenericPreview: {
        width: 100,
        height: 100,
        borderRadius: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer', // For lightbox
        border: `1px solid rgba(255, 255, 255, 0.1)`,
    },
    uploadedAssetFileName: {
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        wordBreak: 'break-word', // Better word breaking
        maxHeight: '2.4em', // Limit to two lines
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: 1.2,
        fontWeight: 500,
    },
    uploadingIndicator: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '50%',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    downloadLink: {
        display: 'inline-block',
        marginTop: 15,
        padding: '10px 15px',
        backgroundColor: Colors.purple,
        color: 'white',
        textDecoration: 'none',
        borderRadius: 5,
        fontWeight: 500,
    },
    assistantFeedbackPrompt: {
        position: 'absolute',
        width: '100%',
        left: 0,
        bottom: '105%',
        backgroundColor: '#2C2135',
        borderRadius: 12,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        transition: 'transform 0.3s ease-out',
    },
    assistantFeedbackPrompt_mobile: {
        flexDirection: 'column',
    },
    feedbackPrompt_hidden: {
        opacity: 0,
        pointerEvents: 'none',
    },
    feedbackPrompt_visible: {
        opacity: 1,
        pointerEvents: 'all',
    },
    feedbackPromptText: {
        color: 'white',
        fontSize: 14,
        fontWeight: 500
    },
    feedbackButtons: {
        display: 'flex',
        gap: 8
    },
    feedbackButton: {
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        borderRadius: 6,
        transition: 'all 0.2s ease'
    },
    // For positioning bottom offset depending on mobileView (adjust inline via style merging when render)
    characterLimitIndicator: {
        color: Colors.red,
        fontSize: 12,
        fontWeight: 600,
        animation: 'fadeInUp 0.2s ease-out',
        marginBottom: 8,
    },
}

export default AssistantChat 